import React, { useEffect, useMemo, useState, useRef, createContext, useContext, Children, Fragment, createElement, Component } from 'react';
import { useUser } from '../contexts/UserContext';
import { DollarSignIcon, HomeIcon, ChevronRightIcon, LayoutDashboardIcon, KanbanSquareIcon, ListIcon, AlertCircleIcon, ClockIcon, CalendarIcon, TrendingUpIcon, UsersIcon, UserIcon, GlobeIcon, BuildingIcon, Building2Icon, NetworkIcon, GripVerticalIcon, PanelRightCloseIcon, PanelRightOpenIcon, Settings2Icon, ChevronDownIcon, SlidersHorizontalIcon, XIcon, CheckCircleIcon, XCircleIcon, SearchIcon, FilterIcon, MoreVerticalIcon, PlusIcon, ArrowUpDownIcon, ArrowUpIcon, ArrowDownIcon, DownloadIcon, FileTextIcon, StickyNoteIcon, ChevronLeftIcon, EyeIcon, MessageSquareIcon, SendIcon, LockIcon } from 'lucide-react';
// --- Types ---
type NavItem = 'overview' | 'pipeline' | 'quotes' | 'business-units' | 'dealers' | 'attention' | 'followups' | 'timeline';
type QuoteStatus = 'Draft' | 'Active' | 'Expired' | 'Won' | 'Lost' | 'Cancelled';
type QuoteProductType = 'pivot' | 'spareparts' | 'project';
interface Quote {
  id: string;
  customer: string;
  dealer: string;
  country: string;
  amount: number;
  probability: number;
  status: QuoteStatus;
  date: string;
  lastUpdated: string;
  needsAttention?: boolean;
  attentionReason?: string;
  businessUnit: string;
  salesManager: string;
  // Optional refined fields — fall back to derived values when missing
  name?: string;
  quotedBy?: string;
  expiresAt?: string;
  productType?: QuoteProductType;
  productCount?: number;
  notes?: QuoteNote[];
}
// --- Helpers for derived quote fields ---
const PRODUCT_TYPE_LABEL: Record<QuoteProductType, string> = {
  pivot: 'pivot',
  spareparts: 'spare parts',
  project: 'project'
};
function hashCode(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}
function getQuoteName(q: Quote): string {
  if (q.name) return q.name;
  const type = getProductType(q);
  const label = type === 'pivot' ? 'Pivot Package' : type === 'spareparts' ? 'Spare Parts Order' : 'Project';
  return `${q.customer} — ${label}`;
}
function getQuotedBy(q: Quote): string {
  return q.quotedBy ?? q.salesManager;
}
function getProductType(q: Quote): QuoteProductType {
  if (q.productType) return q.productType;
  const types: QuoteProductType[] = ['pivot', 'spareparts', 'project'];
  return types[hashCode(q.id) % types.length];
}
function getProductCount(q: Quote): number {
  if (typeof q.productCount === 'number') return q.productCount;
  const type = getProductType(q);
  const seed = hashCode(q.id);
  if (type === 'pivot') return 1 + seed % 8; // 1–8 pivots
  if (type === 'spareparts') return 5 + seed % 45; // 5–49 parts
  return 1; // 1 project
}
function addDaysISO(iso: string, days: number): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}
function getExpiresAt(q: Quote): string {
  return q.expiresAt ?? addDaysISO(q.date, 30);
}
function formatShortDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return iso;
  }
}
function formatLongDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return iso;
  }
}
// --- Notes & Timeline ---
type NoteKind = 'closure' | 'follow-up' | 'general';
interface QuoteNote {
  kind: NoteKind;
  date: string;
  author: string;
  text: string;
}
type ActivityKind = 'created' | 'updated' | 'note' | 'status-change' | 'discount' | 'items-changed';
interface ActivityEvent {
  date: string;
  kind: ActivityKind;
  title: string;
  detail?: string;
  author?: string;
  // For status-change: target status
  toStatus?: QuoteStatus;
  // For notes: bind the matching note
  note?: QuoteNote;
}
// Realistic loss/cancellation reasons for synthetic notes
const CLOSURE_REASONS_LOST: string[] = ['Lost to competitor on price — John Deere offered ~12% below our quote. Customer prioritised CAPEX over service. Revisit in Q3 with bundled service plan.', 'Customer postponed project to 2027 due to budget cuts. Land already prepared; revisit when financing reopens.', 'Spec changed mid-cycle: customer needed corner system, we couldn’t deliver in time. Lost to local integrator.', 'Financing fell through. Customer chose to lease equipment from a rental dealer instead. Keep warm — interested in 2027.', 'Competitor won on lead time (4 weeks vs our 9). Production capacity issue. Recommend escalating with ops.'];
const CLOSURE_REASONS_CANCELLED: string[] = ['Customer cancelled internally — pivot field was sold off as part of a larger land deal. No replacement project.', 'Cancelled by customer after well water tests showed insufficient flow. Recommended alternative drip system from partner.', 'Cancelled — customer never returned signed PO after multiple follow-ups. Lead went cold.', 'Project scope changed: customer pivoted to greenhouse cultivation. No longer a fit.'];
const CLOSURE_REASONS_WON: string[] = ['Won on bundled service plan + 24-month parts warranty. Beat competitor by adding installation training, no price cut needed.', 'Won despite higher price thanks to existing relationship with Sales Manager. Customer cited service reliability as decisive factor.', 'Closed quickly — customer renewed with us after a positive previous experience. No discount applied.'];
function pickFrom<T>(arr: T[], seed: number): T {
  return arr[Math.abs(seed) % arr.length];
}
function getNotes(q: Quote): QuoteNote[] {
  if (q.notes && q.notes.length > 0) return q.notes;
  const seed = hashCode(q.id);
  // Synthesize closure notes for closed states
  if (q.status === 'Lost') {
    return [{
      kind: 'closure',
      date: q.lastUpdated,
      author: getQuotedBy(q),
      text: pickFrom(CLOSURE_REASONS_LOST, seed)
    }];
  }
  if (q.status === 'Cancelled') {
    return [{
      kind: 'closure',
      date: q.lastUpdated,
      author: getQuotedBy(q),
      text: pickFrom(CLOSURE_REASONS_CANCELLED, seed)
    }];
  }
  if (q.status === 'Won' && seed % 3 === 0) {
    // ~1/3 of Wons get a note
    return [{
      kind: 'closure',
      date: q.lastUpdated,
      author: getQuotedBy(q),
      text: pickFrom(CLOSURE_REASONS_WON, seed)
    }];
  }
  // Occasional follow-up note on Active quotes (~1/7)
  if (q.status === 'Active' && seed % 7 === 0) {
    return [{
      kind: 'follow-up',
      date: q.lastUpdated,
      author: getQuotedBy(q),
      text: 'Customer requested updated spec sheet and a site visit before signing. Follow-up scheduled.'
    }];
  }
  return [];
}
function hasNote(q: Quote): boolean {
  return getNotes(q).length > 0;
}
function getClosureNote(q: Quote): QuoteNote | null {
  return getNotes(q).find(n => n.kind === 'closure') ?? null;
}
// Build a synthetic activity timeline for a quote (descending: most recent first)
function getTimeline(q: Quote): ActivityEvent[] {
  const events: ActivityEvent[] = [];
  // Status-change event if the quote is in a final/changed state
  if (['Won', 'Lost', 'Cancelled', 'Expired'].includes(q.status)) {
    events.push({
      date: q.lastUpdated,
      kind: 'status-change',
      title: `Marked as ${q.status}`,
      toStatus: q.status,
      author: getQuotedBy(q)
    });
  }
  // Notes (closure note inlined here)
  for (const n of getNotes(q)) {
    events.push({
      date: n.date,
      kind: 'note',
      title: n.kind === 'closure' ? 'Closure note added' : n.kind === 'follow-up' ? 'Follow-up note' : 'Note added',
      detail: n.text,
      author: n.author,
      note: n
    });
  }
  // Synth a discount / items event for higher-amount quotes
  const seed = hashCode(q.id);
  if (q.amount > 80000 && seed % 2 === 0) {
    events.push({
      date: addDaysISO(q.date, Math.max(2, seed % 10 + 2)),
      kind: 'discount',
      title: `Discount applied (-${5 + seed % 8}%)`,
      author: getQuotedBy(q)
    });
  }
  if (seed % 3 === 0) {
    events.push({
      date: addDaysISO(q.date, 1 + seed % 5),
      kind: 'items-changed',
      title: 'Items updated',
      author: getQuotedBy(q)
    });
  }
  // Update event if lastUpdated differs from date
  if (q.lastUpdated && q.lastUpdated !== q.date) {
    events.push({
      date: q.lastUpdated,
      kind: 'updated',
      title: 'Quote updated',
      author: getQuotedBy(q)
    });
  }
  // Created (always)
  events.push({
    date: q.date,
    kind: 'created',
    title: 'Quote created',
    author: getQuotedBy(q)
  });
  // Sort descending and deduplicate same date+kind+title by keeping the more informative one
  return events.sort((a, b) => {
    const da = new Date(a.date).getTime();
    const db = new Date(b.date).getTime();
    return db - da;
  });
}
// --- Dummy Data ---
export const dummyQuotes: Quote[] = [
// Aisco EUROPE — Olexandr Morozov
{
  id: 'QT-2026-001',
  customer: 'AgriCorp',
  dealer: 'Zeppelin',
  country: 'Ukraine',
  amount: 125000,
  probability: 80,
  status: 'Active',
  date: '2026-05-01',
  lastUpdated: '2026-05-18',
  needsAttention: true,
  attentionReason: 'Price validity expires in 2 days',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Olexandr Morozov'
}, {
  id: 'QT-2026-003',
  customer: 'Valley Farms',
  dealer: 'Zeppelin',
  country: 'Ukraine',
  amount: 210000,
  probability: 90,
  status: 'Active',
  date: '2026-04-20',
  lastUpdated: '2026-05-17',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Olexandr Morozov'
}, {
  id: 'QT-2026-006',
  customer: 'River Basin',
  dealer: 'Zeppelin',
  country: 'Ukraine',
  amount: 150000,
  probability: 20,
  status: 'Draft',
  date: '2026-05-18',
  lastUpdated: '2026-05-19',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Olexandr Morozov'
},
// Aisco EUROPE — Javier Adrados
{
  id: 'QT-2026-002',
  customer: 'GreenFields',
  dealer: 'AgroTech',
  country: 'Spain',
  amount: 85000,
  probability: 40,
  status: 'Active',
  date: '2026-05-10',
  lastUpdated: '2026-05-15',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Javier Adrados'
}, {
  id: 'QT-2026-005',
  customer: 'Highland',
  dealer: 'AgroTech',
  country: 'Spain',
  amount: 45000,
  probability: 0,
  status: 'Lost',
  date: '2026-03-15',
  lastUpdated: '2026-04-01',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Javier Adrados'
}, {
  id: 'QT-2026-007',
  customer: 'SunCrop',
  dealer: 'AgroTech',
  country: 'Spain',
  amount: 95000,
  probability: 60,
  status: 'Active',
  date: '2026-05-12',
  lastUpdated: '2026-05-12',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Javier Adrados'
},
// Aisco EUROPE — Régis SIMIER
{
  id: 'QT-2026-008',
  customer: 'Champagne Vins',
  dealer: 'IrrigaFrance',
  country: 'France',
  amount: 178000,
  probability: 70,
  status: 'Active',
  date: '2026-05-04',
  lastUpdated: '2026-05-18',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Régis SIMIER'
}, {
  id: 'QT-2026-009',
  customer: 'Loire Vallée',
  dealer: 'IrrigaFrance',
  country: 'France',
  amount: 62000,
  probability: 50,
  status: 'Active',
  date: '2026-05-11',
  lastUpdated: '2026-05-14',
  businessUnit: 'Aisco EUROPE',
  salesManager: 'Régis SIMIER'
},
// Aisco MENA — Mohamed S. Aloraini
{
  id: 'QT-2026-004',
  customer: 'Oasis Ag',
  dealer: 'Desert Bloom',
  country: 'Egypt',
  amount: 340000,
  probability: 100,
  status: 'Won',
  date: '2026-04-05',
  lastUpdated: '2026-05-10',
  businessUnit: 'Aisco MENA',
  salesManager: 'Mohamed S. Aloraini'
}, {
  id: 'QT-2026-010',
  customer: 'Nile Greens',
  dealer: 'Desert Bloom',
  country: 'Egypt',
  amount: 145000,
  probability: 75,
  status: 'Active',
  date: '2026-05-02',
  lastUpdated: '2026-05-17',
  needsAttention: true,
  attentionReason: 'No follow-up activity in 14 days',
  businessUnit: 'Aisco MENA',
  salesManager: 'Mohamed S. Aloraini'
},
// Aisco MENA — John Quirk
{
  id: 'QT-2026-011',
  customer: 'Gulf Agro',
  dealer: 'Saudi Irrigation',
  country: 'Saudi Arabia',
  amount: 420000,
  probability: 85,
  status: 'Active',
  date: '2026-04-15',
  lastUpdated: '2026-05-15',
  businessUnit: 'Aisco MENA',
  salesManager: 'John Quirk'
}, {
  id: 'QT-2026-012',
  customer: 'Red Sea Farms',
  dealer: 'Saudi Irrigation',
  country: 'Saudi Arabia',
  amount: 95000,
  probability: 40,
  status: 'Cancelled',
  date: '2026-05-08',
  lastUpdated: '2026-05-12',
  businessUnit: 'Aisco MENA',
  salesManager: 'John Quirk'
},
// Aisco AMERICA — Diego Solis
{
  id: 'QT-2026-013',
  customer: 'AgroMex',
  dealer: 'Riego Norte',
  country: 'Mexico',
  amount: 188000,
  probability: 65,
  status: 'Active',
  date: '2026-05-06',
  lastUpdated: '2026-05-18',
  businessUnit: 'Aisco AMERICA',
  salesManager: 'Diego Solis'
}, {
  id: 'QT-2026-014',
  customer: 'Sonora Crops',
  dealer: 'Riego Norte',
  country: 'Mexico',
  amount: 76000,
  probability: 100,
  status: 'Won',
  date: '2026-03-20',
  lastUpdated: '2026-04-22',
  businessUnit: 'Aisco AMERICA',
  salesManager: 'Diego Solis'
},
// Aisco AMERICA — Chris Spelic
{
  id: 'QT-2026-015',
  customer: 'Midwest Harvest',
  dealer: 'Iowa Pivot Co',
  country: 'United States',
  amount: 265000,
  probability: 90,
  status: 'Active',
  date: '2026-04-28',
  lastUpdated: '2026-05-16',
  businessUnit: 'Aisco AMERICA',
  salesManager: 'Chris Spelic'
}, {
  id: 'QT-2026-016',
  customer: 'Prairie Ag',
  dealer: 'Iowa Pivot Co',
  country: 'United States',
  amount: 132000,
  probability: 55,
  status: 'Expired',
  date: '2026-05-09',
  lastUpdated: '2026-05-14',
  needsAttention: true,
  attentionReason: 'Quote pending response for 9 days',
  businessUnit: 'Aisco AMERICA',
  salesManager: 'Chris Spelic'
}, {
  id: 'QT-2026-017',
  customer: 'Heartland Farms',
  dealer: "ABE'S IRRIGATION",
  country: 'United States',
  amount: 89000,
  probability: 30,
  status: 'Active',
  date: '2026-05-14',
  lastUpdated: '2026-05-14',
  businessUnit: 'Aisco AMERICA',
  salesManager: 'Chris Spelic'
},
// Aisco AFRICA — Khaled Shalabi
{
  id: 'QT-2026-018',
  customer: 'Sahara Greens',
  dealer: 'Maghreb Agro',
  country: 'Morocco',
  amount: 156000,
  probability: 60,
  status: 'Active',
  date: '2026-05-03',
  lastUpdated: '2026-05-15',
  businessUnit: 'Aisco AFRICA',
  salesManager: 'Khaled Shalabi'
}, {
  id: 'QT-2026-019',
  customer: 'Atlas Farms',
  dealer: 'Maghreb Agro',
  country: 'Morocco',
  amount: 68000,
  probability: 0,
  status: 'Lost',
  date: '2026-02-28',
  lastUpdated: '2026-03-25',
  businessUnit: 'Aisco AFRICA',
  salesManager: 'Khaled Shalabi'
}];
export const QuotesContext = createContext<Quote[]>(dummyQuotes);
export function useQuotes() {
  return useContext(QuotesContext);
}
// --- Helper Components ---
function StatusBadge({
  status
}: {
  status: QuoteStatus;
}) {
  const colors: Record<QuoteStatus, string> = {
    Draft: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    Active: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    Won: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    Lost: 'bg-red-500/20 text-red-300 border-red-500/30',
    Expired: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    Cancelled: 'bg-gray-500/20 text-gray-300 border-gray-500/30'
  };
  return <span className={`px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${colors[status]}`} data-id="element-11935">
      {status}
    </span>;
}
function KPICard({
  title,
  value,
  subtitle,
  icon,
  trend
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}) {
  return <div className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm" data-id="element-11936">
      <div className="flex items-start justify-between mb-2" data-id="element-11937">
        <div className="p-2 bg-white/5 rounded-lg text-blue-400" data-id="element-11938">{icon}</div>
        {trend && <div className={`flex items-center text-xs font-medium ${trend.isPositive ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-11939">
            {trend.isPositive ? <TrendingUpIcon size={14} className="mr-1" data-id="element-11940" /> : <TrendingUpIcon size={14} className="mr-1 rotate-180" data-id="element-11941" />}
            {trend.value}%
          </div>}
      </div>
      <h3 className="text-2xl font-bold text-white mb-1" data-id="element-11942">{value}</h3>
      <p className="text-sm text-blue-300/70" data-id="element-11943">{title}</p>
      {subtitle && <p className="text-xs text-blue-300/50 mt-1" data-id="element-11944">{subtitle}</p>}
    </div>;
}
// --- Views ---
function OverviewView({
  role
}: {
  role: string;
}) {
  const dummyQuotes = useQuotes();
  const openPipeline = dummyQuotes.filter(q => !['Won', 'Lost', 'Expired', 'Cancelled'].includes(q.status)).reduce((acc, q) => acc + q.amount, 0);
  const weightedPipeline = dummyQuotes.filter(q => !['Won', 'Lost', 'Expired', 'Cancelled'].includes(q.status)).reduce((acc, q) => acc + q.amount * q.probability / 100, 0);
  const wonAmount = dummyQuotes.filter(q => q.status === 'Won').reduce((acc, q) => acc + q.amount, 0);
  let title = 'Global Overview';
  if (['manager-business-unit', 'region-admin'].includes(role)) title = 'BU Control Center';
  if (role === 'sales-manager') title = 'Territory Workbench';
  if (['dealer', 'dealer-admin'].includes(role)) title = 'My Quotes';
  return <div className="space-y-6" data-id="element-11945">
      <div className="flex items-center justify-between" data-id="element-11946">
        <h2 className="text-xl font-bold text-white" data-id="element-11947">{title}</h2>
        <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-11948">
          <PlusIcon size={16} data-id="element-11949" />
          New Quote
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-11950">
        <KPICard title="Open Pipeline" value={`$${openPipeline.toLocaleString()}`} icon={<DollarSignIcon size={20} data-id="element-11952" />} trend={{
        value: 12.5,
        isPositive: true
      }} data-id="element-11951" />
        <KPICard title="Weighted Pipeline" value={`$${weightedPipeline.toLocaleString()}`} icon={<TrendingUpIcon size={20} data-id="element-11954" />} data-id="element-11953" />
        <KPICard title="Won (YTD)" value={`$${wonAmount.toLocaleString()}`} icon={<CheckCircleIcon size={20} data-id="element-11956" />} trend={{
        value: 5.2,
        isPositive: true
      }} data-id="element-11955" />
        <KPICard title="Win Rate" value="42%" icon={<TrendingUpIcon size={20} data-id="element-11958" />} data-id="element-11957" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-11959">
        <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm" data-id="element-11960">
          <h3 className="text-lg font-semibold text-white mb-4" data-id="element-11961">
            Recent Activity
          </h3>
          <div className="space-y-4" data-id="element-11962">
            {dummyQuotes.slice(0, 4).map(quote => <div key={quote.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors border border-white/5" data-id="element-11963">
                <div className="flex items-center gap-4" data-id="element-11964">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400" data-id="element-11965">
                    <DollarSignIcon size={18} data-id="element-11966" />
                  </div>
                  <div data-id="element-11967">
                    <p className="text-sm font-medium text-white" data-id="element-11968">
                      {quote.customer}
                    </p>
                    <p className="text-xs text-blue-300/70" data-id="element-11969">
                      {quote.id} • {quote.dealer}
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-11970">
                  <p className="text-sm font-bold text-white" data-id="element-11971">
                    ${quote.amount.toLocaleString()}
                  </p>
                  <StatusBadge status={quote.status} data-id="element-11972" />
                </div>
              </div>)}
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm" data-id="element-11973">
          <div className="flex items-center gap-2 mb-4 text-amber-400" data-id="element-11974">
            <AlertCircleIcon size={20} data-id="element-11975" />
            <h3 className="text-lg font-semibold text-white" data-id="element-11976">
              Needs Attention
            </h3>
          </div>
          <div className="space-y-3" data-id="element-11977">
            {dummyQuotes.filter(q => q.needsAttention).map(quote => <div key={quote.id} className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20" data-id="element-11978">
                  <div className="flex justify-between items-start mb-1" data-id="element-11979">
                    <p className="text-sm font-medium text-white" data-id="element-11980">
                      {quote.customer}
                    </p>
                    <span className="text-xs text-amber-400 font-medium" data-id="element-11981">
                      {quote.id}
                    </span>
                  </div>
                  <p className="text-xs text-amber-200/70 mb-2" data-id="element-11982">
                    {quote.attentionReason}
                  </p>
                  <button className="text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 px-3 py-1.5 rounded transition-colors w-full font-medium" data-id="element-11983">
                    Review Quote
                  </button>
                </div>)}
            {dummyQuotes.filter(q => q.needsAttention).length === 0 && <p className="text-sm text-blue-300/50 text-center py-4" data-id="element-11984">
                All caught up!
              </p>}
          </div>
        </div>
      </div>
    </div>;
}
function PipelineView() {
  const dummyQuotes = useQuotes();
  const columns: {
    title: string;
    statuses: QuoteStatus[];
  }[] = [{
    title: 'Draft',
    statuses: ['Draft']
  }, {
    title: 'Active',
    statuses: ['Active']
  }, {
    title: 'Expired',
    statuses: ['Expired']
  }, {
    title: 'Won',
    statuses: ['Won']
  }, {
    title: 'Lost',
    statuses: ['Lost']
  }, {
    title: 'Cancelled',
    statuses: ['Cancelled']
  }];
  return <div className="h-full flex flex-col" data-id="element-11985">
      <div className="flex items-center justify-between mb-6" data-id="element-11986">
        <h2 className="text-xl font-bold text-white" data-id="element-11987">Sales Pipeline</h2>
        <div className="flex items-center gap-2" data-id="element-11988">
          <div className="relative" data-id="element-11989">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300/50" data-id="element-11990" />
            <input type="text" placeholder="Search quotes..." className="bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-11991" />
          </div>
          <button className="p-2 bg-white/5 border border-white/10 rounded-lg text-blue-300 hover:text-white hover:bg-white/10 transition-colors" data-id="element-11992">
            <FilterIcon size={18} data-id="element-11993" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-x-auto pb-4 no-scrollbar" data-id="element-11994">
        <div className="flex gap-4 min-w-max h-full" data-id="element-11995">
          {columns.map(col => {
          const colQuotes = dummyQuotes.filter(q => col.statuses.includes(q.status));
          const colTotal = colQuotes.reduce((acc, q) => acc + q.amount, 0);
          return <div key={col.title} className="w-80 flex flex-col bg-white/5 border border-white/10 rounded-xl overflow-hidden flex-shrink-0" data-id="element-11996">
                <div className="p-3 border-b border-white/10 bg-black/20 flex items-center justify-between" data-id="element-11997">
                  <h3 className="text-sm font-semibold text-white" data-id="element-11998">
                    {col.title}{' '}
                    <span className="text-blue-300/50 ml-1 font-normal" data-id="element-11999">
                      ({colQuotes.length})
                    </span>
                  </h3>
                  <span className="text-xs font-medium text-blue-200" data-id="element-12000">
                    ${(colTotal / 1000).toFixed(1)}k
                  </span>
                </div>
                <div className="flex-1 p-3 overflow-y-auto space-y-3 no-scrollbar" data-id="element-12001">
                  {colQuotes.map(quote => <div key={quote.id} className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/10 transition-colors cursor-pointer group" data-id="element-12002">
                      <div className="flex justify-between items-start mb-2" data-id="element-12003">
                        <span className="text-xs font-medium text-blue-400" data-id="element-12004">
                          {quote.id}
                        </span>
                        <StatusBadge status={quote.status} data-id="element-12005" />
                      </div>
                      <p className="text-sm font-semibold text-white mb-1" data-id="element-12006">
                        {quote.customer}
                      </p>
                      <p className="text-xs text-blue-300/70 mb-3" data-id="element-12007">
                        {quote.dealer}
                      </p>
                      <div className="flex items-center justify-between pt-2 border-t border-white/5" data-id="element-12008">
                        <span className="text-sm font-bold text-white" data-id="element-12009">
                          ${quote.amount.toLocaleString()}
                        </span>
                        <span className="text-xs font-medium text-blue-300 bg-blue-500/20 px-1.5 py-0.5 rounded" data-id="element-12010">
                          {quote.probability}%
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
}
// --- All Quotes Explorer (unified configurable view) ---
type GroupDimension = 'businessUnit' | 'salesManager' | 'dealer' | 'customer' | 'country' | 'status' | 'probability';
const DIMENSION_META: Record<GroupDimension, {
  label: string;
  icon: React.ReactNode;
  accessor: (q: Quote) => string;
}> = {
  businessUnit: {
    label: 'Business Unit',
    icon: <Building2Icon size={14} data-id="element-12011" />,
    accessor: q => q.businessUnit
  },
  salesManager: {
    label: 'Sales Manager',
    icon: <UserIcon size={14} data-id="element-12012" />,
    accessor: q => q.salesManager
  },
  dealer: {
    label: 'Dealer',
    icon: <BuildingIcon size={14} data-id="element-12013" />,
    accessor: q => q.dealer
  },
  customer: {
    label: 'Customer',
    icon: <UsersIcon size={14} data-id="element-12014" />,
    accessor: q => q.customer
  },
  country: {
    label: 'Country',
    icon: <GlobeIcon size={14} data-id="element-12015" />,
    accessor: q => q.country
  },
  status: {
    label: 'Status',
    icon: <CheckCircleIcon size={14} data-id="element-12016" />,
    accessor: q => q.status
  },
  probability: {
    label: 'Probability',
    icon: <TrendingUpIcon size={14} data-id="element-12017" />,
    accessor: q => {
      if (q.probability > 70) return 'High (>70%)';
      if (q.probability >= 30) return 'Medium (30–70%)';
      return 'Low (<30%)';
    }
  }
};
function getDefaultChain(role: string): GroupDimension[] {
  if (['ceo', 'system-admin'].includes(role)) return ['businessUnit', 'salesManager', 'dealer'];
  if (['manager-business-unit', 'region-admin'].includes(role)) return ['salesManager', 'dealer'];
  if (role === 'sales-manager') return ['dealer', 'customer'];
  if (['dealer', 'dealer-admin'].includes(role)) return ['customer'];
  return ['businessUnit'];
}
// Human-readable label per role for UI copy
function getRoleLabel(role: string): string {
  const map: Record<string, string> = {
    ceo: 'CEO',
    'system-admin': 'System Admin',
    'manager-business-unit': 'Business Unit Manager',
    'region-admin': 'Region Admin',
    'sales-manager': 'Sales Manager',
    dealer: 'Dealer',
    'dealer-admin': 'Dealer Admin',
    customer: 'Customer',
    'operations-manager': 'Operations Manager',
    logistic: 'Logistics',
    marketing: 'Marketing',
    'financial-manager': 'Financial Manager',
    'authenticated-public-user': 'Authenticated User'
  };
  return map[role] ?? role;
}
interface TreeGroup {
  key: string;
  label: string;
  dimension: GroupDimension;
  quotes: Quote[];
  children: TreeGroup[];
  totalAmount: number;
  openAmount: number;
  weighted: number;
  wonAmount: number;
  wonCount: number;
  lostCount: number;
  winRate: number;
  needsAttention: number;
}
function computeAggregates(quotes: Quote[]) {
  const closed: QuoteStatus[] = ['Won', 'Lost', 'Expired', 'Cancelled'];
  let totalAmount = 0;
  let openAmount = 0;
  let weighted = 0;
  let wonAmount = 0;
  let wonCount = 0;
  let lostCount = 0;
  let needsAttention = 0;
  quotes.forEach(q => {
    totalAmount += q.amount;
    if (!closed.includes(q.status)) {
      openAmount += q.amount;
      weighted += q.amount * q.probability / 100;
    }
    if (q.status === 'Won') {
      wonAmount += q.amount;
      wonCount += 1;
    }
    if (q.status === 'Lost' || q.status === 'Expired') lostCount += 1;
    if (q.needsAttention) needsAttention += 1;
  });
  const decided = wonCount + lostCount;
  const winRate = decided > 0 ? Math.round(wonCount / decided * 100) : 0;
  return {
    totalAmount,
    openAmount,
    weighted,
    wonAmount,
    wonCount,
    lostCount,
    winRate,
    needsAttention
  };
}
function buildTree(quotes: Quote[], chain: GroupDimension[], depth: number, parentPath: string): TreeGroup[] {
  if (depth >= chain.length) return [];
  const dim = chain[depth];
  const accessor = DIMENSION_META[dim].accessor;
  const buckets = new Map<string, Quote[]>();
  quotes.forEach(q => {
    const key = accessor(q);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(q);
  });
  const groups: TreeGroup[] = Array.from(buckets.entries()).map(([label, groupQuotes]) => {
    const path = `${parentPath}/${dim}:${label}`;
    const aggs = computeAggregates(groupQuotes);
    return {
      key: path,
      label,
      dimension: dim,
      quotes: groupQuotes,
      children: buildTree(groupQuotes, chain, depth + 1, path),
      ...aggs
    };
  });
  // Sort each level by openAmount desc by default
  return groups.sort((a, b) => b.openAmount - a.openAmount);
}
function GroupChainChip({
  dimension,
  onRemove,
  canRemove,
  index,
  isDragging,
  isDropTarget,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onDragLeave
}: {
  dimension: GroupDimension;
  onRemove: () => void;
  canRemove: boolean;
  index: number;
  isDragging: boolean;
  isDropTarget: boolean;
  onDragStart: (index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  onDragLeave: () => void;
}) {
  const meta = DIMENSION_META[dimension];
  return <div draggable onDragStart={() => onDragStart(index)} onDragOver={e => onDragOver(e, index)} onDrop={e => onDrop(e, index)} onDragEnd={onDragEnd} onDragLeave={onDragLeave} className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium cursor-grab active:cursor-grabbing select-none transition-all ${isDragging ? 'opacity-40 bg-blue-500/30 border-blue-400/60' : isDropTarget ? 'bg-blue-500/30 border-blue-300 ring-2 ring-blue-400/40 text-white' : 'bg-blue-500/15 border-blue-400/30 text-blue-100 hover:bg-blue-500/20'}`} data-id="element-12018">
      <GripVerticalIcon size={12} className="text-blue-300/60 -ml-0.5" aria-hidden data-id="element-12019" />
      <span className="text-blue-300" data-id="element-12020">{meta.icon}</span>
      {meta.label}
      {canRemove && <button onClick={e => {
      e.stopPropagation();
      onRemove();
    }} onMouseDown={e => e.stopPropagation()} className="ml-0.5 text-blue-300/70 hover:text-white rounded-full hover:bg-blue-500/20 w-4 h-4 flex items-center justify-center" aria-label={`Remove ${meta.label} grouping`} data-id="element-12021">
          ×
        </button>}
    </div>;
}
function AddLevelDropdown({
  available,
  onAdd
}: {
  available: GroupDimension[];
  onAdd: (d: GroupDimension) => void;
}) {
  const [open, setOpen] = useState(false);
  if (available.length === 0) return null;
  return <div className="relative" data-id="element-12022">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 border border-dashed border-white/20 text-blue-300 hover:bg-white/10 hover:text-white hover:border-blue-400/40 text-xs font-medium transition-colors" data-id="element-12023">
        <PlusIcon size={12} data-id="element-12024" />
        Add level
      </button>
      {open && <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} data-id="element-12025" />
          <div className="absolute top-full left-0 mt-1 z-50 w-48 bg-slate-900 border border-white/10 rounded-lg shadow-xl py-1" data-id="element-12026">
            {available.map(d => {
          const meta = DIMENSION_META[d];
          return <button key={d} onClick={() => {
            onAdd(d);
            setOpen(false);
          }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-blue-100 hover:bg-white/5 transition-colors text-left" data-id="element-12027">
                  <span className="text-blue-400" data-id="element-12028">{meta.icon}</span>
                  {meta.label}
                </button>;
        })}
          </div>
        </>}
    </div>;
}
// Status dot colors used across KPI breakdown and tree rows for consistency
const STATUS_DOT_COLORS: Record<QuoteStatus, string> = {
  Draft: 'bg-slate-400',
  Active: 'bg-blue-400',
  Expired: 'bg-rose-400',
  Won: 'bg-emerald-400',
  Lost: 'bg-red-400',
  Cancelled: 'bg-gray-400'
};
function TreeRow({
  node,
  depth,
  expanded,
  onToggle,
  isLast,
  statusesToShow,
  onOpenQuote,
  onQuoteAction,
  selectedQuoteId
}: {
  node: TreeGroup;
  depth: number;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  isLast: boolean;
  statusesToShow: QuoteStatus[];
  onOpenQuote?: (id: string) => void;
  onQuoteAction?: (id: string, action: 'view' | 'timeline' | 'notes') => void;
  selectedQuoteId?: string | null;
}) {
  const isOpen = expanded.has(node.key);
  const meta = DIMENSION_META[node.dimension];
  const hasChildren = node.children.length > 0;
  // When no children configured (we hit chain end), show quotes as leaves
  return <>
      <div onClick={() => onToggle(node.key)} className={`flex items-center gap-3 py-2.5 px-3 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${isOpen ? 'bg-blue-500/5' : ''}`} style={{
      paddingLeft: `${12 + depth * 20}px`
    }} data-id="element-12029">
        <ChevronRightIcon size={14} className={`flex-shrink-0 transition-transform ${isOpen ? 'rotate-90 text-blue-400' : 'text-blue-300/60'}`} data-id="element-12030" />
        <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-12031">
          <span className="text-blue-400 flex-shrink-0" data-id="element-12032">{meta.icon}</span>
          <div className="min-w-0" data-id="element-12033">
            <p className="text-sm font-semibold text-white truncate" data-id="element-12034">
              {node.label}
            </p>
            <p className="text-[10px] text-blue-300/50 uppercase tracking-wider font-medium" data-id="element-12035">
              {meta.label}
              {node.needsAttention > 0 && <span className="ml-1.5 text-amber-400" data-id="element-12036">
                  · {node.needsAttention} alert
                  {node.needsAttention !== 1 ? 's' : ''}
                </span>}
            </p>
          </div>
        </div>
        {node.dimension === 'status' ? <div className="hidden md:flex items-center gap-5 flex-shrink-0 text-right" data-id="element-12037">
            <div className="w-14" data-id="element-12038">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/50 font-semibold" data-id="element-12039">
                Total
              </p>
              <p className="text-sm font-bold text-white tabular-nums" data-id="element-12040">
                {node.quotes.length}
              </p>
            </div>
            <div className="w-28" data-id="element-12041">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/50 font-semibold" data-id="element-12042">
                Importe Total
              </p>
              <p className="text-sm font-bold text-white tabular-nums" data-id="element-12043">
                ${node.totalAmount.toLocaleString()}
              </p>
            </div>
          </div> : <div className="hidden md:flex items-center gap-5 flex-shrink-0 text-right" data-id="element-12044">
            <div className="w-14" data-id="element-12045">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/50 font-semibold" data-id="element-12046">
                Total
              </p>
              <p className="text-sm font-bold text-white tabular-nums" data-id="element-12047">
                {node.quotes.length}
              </p>
            </div>
            {statusesToShow.map(s => {
          const matching = node.quotes.filter(q => q.status === s);
          const count = matching.length;
          const amount = matching.reduce((acc, q) => acc + q.amount, 0);
          return <div key={s} className="w-24" data-id="element-12048">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/50 font-semibold flex items-center justify-end gap-1" data-id="element-12049">
                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT_COLORS[s]}`} data-id="element-12050" />
                    {s}
                  </p>
                  <p className="text-sm font-bold text-white tabular-nums" data-id="element-12051">
                    {count}
                  </p>
                  <p className="text-[11px] text-blue-300/70 tabular-nums" data-id="element-12052">
                    ${amount.toLocaleString()}
                  </p>
                </div>;
        })}
            <div className="w-28" data-id="element-12053">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/50 font-semibold" data-id="element-12054">
                Importe Total
              </p>
              <p className="text-sm font-bold text-white tabular-nums" data-id="element-12055">
                ${node.totalAmount.toLocaleString()}
              </p>
            </div>
          </div>}
      </div>
      {isOpen && hasChildren && <div data-id="element-12056">
          {node.children.map((child, i) => <TreeRow key={child.key} node={child} depth={depth + 1} expanded={expanded} onToggle={onToggle} isLast={i === node.children.length - 1} statusesToShow={statusesToShow} onOpenQuote={onOpenQuote} onQuoteAction={onQuoteAction} selectedQuoteId={selectedQuoteId} data-id="element-12057" />)}
        </div>}
      {isOpen && !hasChildren && <div data-id="element-12058">
          {node.quotes.map(q => <QuoteLeafRow key={q.id} quote={q} depth={depth + 1} onOpen={onOpenQuote} onAction={onQuoteAction} isSelected={q.id === selectedQuoteId} data-id="element-12059" />)}
        </div>}
    </>;
}
function QuoteLeafRow({
  quote,
  depth,
  onOpen,
  onAction,
  isSelected
}: {
  quote: Quote;
  depth: number;
  onOpen?: (id: string) => void;
  onAction?: (id: string, action: 'view' | 'timeline' | 'notes') => void;
  isSelected?: boolean;
}) {
  const productType = getProductType(quote);
  const productCount = getProductCount(quote);
  const productLabel = PRODUCT_TYPE_LABEL[productType];
  const expiresAt = getExpiresAt(quote);
  const now = new Date();
  const expDate = new Date(expiresAt);
  const isExpiringSoon = !isNaN(expDate.getTime()) && expDate.getTime() - now.getTime() < 1000 * 60 * 60 * 24 * 7 && !['Won', 'Lost', 'Cancelled', 'Expired'].includes(quote.status);
  const noteAvailable = hasNote(quote);
  const noteColor = quote.status === 'Lost' || quote.status === 'Cancelled' ? 'text-rose-300 hover:text-rose-200' : quote.status === 'Won' ? 'text-emerald-300 hover:text-emerald-200' : 'text-blue-300 hover:text-blue-200';
  const [menuOpen, setMenuOpen] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  // Auto-scroll into view when this row becomes selected (e.g. via prev/next in drawer)
  useEffect(() => {
    if (isSelected && rowRef.current) {
      rowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [isSelected]);
  return <div ref={rowRef} role={onOpen ? 'button' : undefined} tabIndex={onOpen ? 0 : undefined} onClick={onOpen ? () => onOpen(quote.id) : undefined} onKeyDown={onOpen ? e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(quote.id);
    }
  } : undefined} className={`flex flex-wrap items-center gap-x-4 gap-y-2 py-2 px-3 border-b border-white/5 transition-colors ${onOpen ? 'cursor-pointer' : ''} ${isSelected ? 'bg-blue-500/15 hover:bg-blue-500/20 border-l-2 border-l-blue-400' : 'hover:bg-white/10'}`} style={{
    paddingLeft: `${12 + depth * 20 - (isSelected ? 2 : 0)}px`
  }} data-id="element-12060">
      {/* ── GROUP 1 · Identidad principal — status + code/name (kept tight together) + customer ── */}
      <div className="flex items-center gap-3 flex-1 min-w-[220px]" data-id="element-12061">
        {/* 1a. Status badge + note icon (auto width so code/name sits right beside it) */}
        <div className="flex-shrink-0 flex items-center gap-1.5" data-id="element-12062">
          <StatusBadge status={quote.status} data-id="element-12063" />
          {noteAvailable && <button type="button" onClick={e => {
          e.stopPropagation();
          onOpen?.(quote.id);
        }} className={`flex-shrink-0 ${noteColor} transition-colors`} title="View notes & timeline" aria-label={`View notes for ${quote.id}`} data-id="element-12064">
              <FileTextIcon size={14} data-id="element-12065" />
            </button>}
        </div>
        {/* 1b. Quote code (top) + Quote name (bottom) */}
        <div className="flex-1 min-w-0" data-id="element-12066">
          <p className="text-xs font-mono text-blue-400 truncate" data-id="element-12067">{quote.id}</p>
          <p className="text-sm text-white font-medium truncate" data-id="element-12068">
            {getQuoteName(quote)}
          </p>
        </div>
        {/* 1c. Dealer & Customer — dealer-only, or dealer + final customer */}
        <div className="w-40 flex-shrink-0 min-w-0 hidden lg:block" data-id="element-12069">
          {quote.dealer && quote.customer ? <>
              <p className="text-sm text-white font-medium truncate" data-id="element-12070">
                {quote.dealer}
              </p>
              <p className="text-[11px] text-blue-300/60 truncate" data-id="element-12071">
                Customer: {quote.customer}
              </p>
            </> : quote.dealer ? <>
              <p className="text-sm text-white font-medium truncate" data-id="element-12072">
                {quote.dealer}
              </p>
              <p className="text-[11px] text-blue-300/50 truncate" data-id="element-12073">Dealer</p>
            </> : <>
              <p className="text-sm text-white font-medium truncate" data-id="element-12074">
                {quote.customer}
              </p>
              <p className="text-[11px] text-blue-300/50 truncate" data-id="element-12075">
                Direct customer
              </p>
            </>}
        </div>
      </div>

      {/* ── Metadata groups (scope + cycle + indicators) — pack together, then wrap below as a block ── */}
      <div className="flex items-center gap-4 flex-wrap" data-id="element-12076">
        {/* GROUP 2 · Alcance — type + quantity summary */}
        <div className="w-24 flex-shrink-0 hidden lg:block" data-id="element-12077">
          <p className="text-[11px] font-semibold text-blue-100 truncate" data-id="element-12078">
            {productLabel}
          </p>
          <p className="text-[11px] text-blue-300/60 tabular-nums" data-id="element-12079">
            {productCount} {productCount === 1 ? 'unit' : 'units'}
          </p>
        </div>

        {/* GROUP 3 · Ciclo comercial — created + expires */}
        <div className="w-44 flex-shrink-0 hidden md:block border-l border-white/5 pl-4" data-id="element-12080">
          <p className="text-[11px] text-blue-300/60 tabular-nums" data-id="element-12081">
            Created {formatShortDate(quote.date)}
          </p>
          {(() => {
          const isExpired = quote.status === 'Expired';
          const daysExpired = isExpired ? Math.max(0, Math.floor((now.getTime() - expDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;
          const cls = isExpired ? 'text-rose-300' : isExpiringSoon ? 'text-amber-300' : 'text-blue-300/60';
          return <p className={`text-[11px] tabular-nums ${cls}`} data-id="element-12082">
                Expires {formatShortDate(expiresAt)}
                {isExpired && <span className="ml-1 text-rose-400/80 font-medium" data-id="element-12083">
                    ({daysExpired} {daysExpired === 1 ? 'day' : 'days'} ago)
                  </span>}
              </p>;
        })()}
        </div>
        {/* GROUP 3 · Quoted by */}
        <div className="w-36 flex-shrink-0 min-w-0 hidden xl:block" data-id="element-12084">
          <p className="text-[10px] uppercase tracking-wider text-blue-400/50 font-semibold" data-id="element-12085">
            Quoted by
          </p>
          <p className="text-xs text-blue-100 truncate" data-id="element-12086">{getQuotedBy(quote)}</p>
        </div>

        {/* GROUP 4 · Indicadores comerciales — probability + amount + alert */}
        <div className="flex items-center gap-4 border-l border-white/5 pl-4" data-id="element-12087">
          <div className="w-16 flex-shrink-0 flex justify-end" data-id="element-12088">
            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded border tabular-nums ${quote.probability > 70 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : quote.probability >= 30 ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' : 'bg-rose-500/20 text-rose-300 border-rose-500/30'}`} title="Win probability" data-id="element-12089">
              {quote.probability}%
            </span>
          </div>
          <div className="w-24 text-right flex-shrink-0" data-id="element-12090">
            <p className="text-sm text-white font-semibold tabular-nums" data-id="element-12091">
              ${quote.amount.toLocaleString()}
            </p>
          </div>
          <div className="w-6 flex-shrink-0 flex justify-center" data-id="element-12092">
            {quote.needsAttention && <AlertCircleIcon size={14} className="text-amber-400" aria-label={quote.attentionReason ?? 'Needs attention'} data-id="element-12093" />}
          </div>
        </div>
      </div>

      {/* ── GROUP 5 · Acciones — always pinned right & visible (ml-auto) ── */}
      <div className="ml-auto flex-shrink-0 flex items-center gap-1" data-id="element-12094">
        <button type="button" onClick={e => {
        e.stopPropagation();
        onAction?.(quote.id, 'view');
      }} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-600/20 hover:bg-blue-600/40 text-blue-300 hover:text-blue-200 transition-colors text-xs font-medium" data-id="element-12095">
          <EyeIcon size={12} data-id="element-12096" />
          <span className="hidden sm:inline" data-id="element-12097">View</span>
        </button>
        <div className="relative" data-id="element-12098">
          <button type="button" onClick={e => {
          e.stopPropagation();
          setMenuOpen(o => !o);
        }} aria-label="Quote actions" aria-haspopup="menu" aria-expanded={menuOpen} className="w-7 h-7 flex items-center justify-center rounded text-blue-300 hover:text-white hover:bg-white/10 transition-colors" title="Actions" data-id="element-12099">
            <MoreVerticalIcon size={14} data-id="element-12100" />
          </button>
          {menuOpen && <>
              <div className="fixed inset-0 z-30" onClick={e => {
            e.stopPropagation();
            setMenuOpen(false);
          }} data-id="element-12101" />
              <div className="absolute right-0 top-full mt-1 z-40 w-40 bg-slate-900 border border-white/10 rounded-md shadow-lg overflow-hidden" data-id="element-12102">
                {[{
              action: 'timeline' as const,
              label: 'Timeline',
              icon: <ClockIcon size={12} data-id="element-12103" />
            }, {
              action: 'notes' as const,
              label: 'Notes',
              icon: <MessageSquareIcon size={12} data-id="element-12104" />
            }].map(item => <button key={item.action} type="button" onClick={e => {
              e.stopPropagation();
              setMenuOpen(false);
              onAction?.(quote.id, item.action);
            }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-blue-100 hover:bg-white/10 hover:text-white transition-colors text-left" data-id="element-12105">
                    <span className="text-blue-300/70 flex-shrink-0" data-id="element-12106">
                      {item.icon}
                    </span>
                    {item.label}
                  </button>)}
              </div>
            </>}
        </div>
      </div>
    </div>;
}
interface QuoteFilters {
  status: QuoteStatus[];
  businessUnit: string[];
  salesManager: string[];
  country: string[];
  dealer: string[];
  probabilityBucket: ('high' | 'medium' | 'low')[];
  quotedByMe: boolean;
}
// --- Date Range ---
type DateRangeMode = 'last12m' | 'year' | 'month';
interface DateRange {
  mode: DateRangeMode;
  year: number;
  month?: number; // 1-12 when mode === 'month'
}
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DEFAULT_DATE_RANGE: DateRange = {
  mode: 'last12m',
  year: new Date().getFullYear()
};
function isDefaultDateRange(r: DateRange): boolean {
  return r.mode === 'last12m';
}
function formatDateRangeLabel(r: DateRange): string {
  if (r.mode === 'last12m') return 'Last 12 months';
  if (r.mode === 'year') return `${r.year}`;
  if (r.mode === 'month' && r.month) return `${MONTH_NAMES[r.month - 1]} ${r.year}`;
  return '';
}
function quoteMatchesDateRange(q: Quote, r: DateRange): boolean {
  const d = new Date(q.date);
  if (isNaN(d.getTime())) return true;
  if (r.mode === 'last12m') {
    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - 1);
    return d >= cutoff;
  }
  if (r.mode === 'year') return d.getFullYear() === r.year;
  if (r.mode === 'month' && r.month) return d.getFullYear() === r.year && d.getMonth() + 1 === r.month;
  return true;
}
// --- Sorting ---
type SortKey = 'id' | 'date' | 'expiresAt' | 'amount' | 'probability' | 'customer' | 'dealer' | 'quotedBy' | 'productCount';
type SortDir = 'asc' | 'desc';
const SORT_OPTIONS: {
  key: SortKey;
  label: string;
}[] = [{
  key: 'date',
  label: 'Created date'
}, {
  key: 'expiresAt',
  label: 'Expires date'
}, {
  key: 'amount',
  label: 'Amount'
}, {
  key: 'id',
  label: 'Code'
}, {
  key: 'customer',
  label: 'Customer'
}, {
  key: 'dealer',
  label: 'Dealer'
}, {
  key: 'quotedBy',
  label: 'Quoted by'
}, {
  key: 'productCount',
  label: 'Product count'
}, {
  key: 'probability',
  label: 'Probability'
}];
function sortQuotes(quotes: Quote[], {
  key,
  dir
}: {
  key: SortKey;
  dir: SortDir;
}): Quote[] {
  const mul = dir === 'asc' ? 1 : -1;
  const getValue = (q: Quote): number | string => {
    switch (key) {
      case 'id':
        return q.id;
      case 'date':
        return new Date(q.date).getTime();
      case 'expiresAt':
        return new Date(getExpiresAt(q)).getTime();
      case 'amount':
        return q.amount;
      case 'probability':
        return q.probability;
      case 'customer':
        return q.customer;
      case 'dealer':
        return q.dealer;
      case 'quotedBy':
        return getQuotedBy(q);
      case 'productCount':
        return getProductCount(q);
    }
  };
  return [...quotes].sort((a, b) => {
    const va = getValue(a);
    const vb = getValue(b);
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mul;
    return String(va).localeCompare(String(vb)) * mul;
  });
}
// --- Export to CSV (Excel-compatible) ---
function exportQuotesToCsv(quotes: Quote[]) {
  const headers = ['Code', 'Name', 'Status', 'Customer', 'Dealer', 'Quoted by', 'Business Unit', 'Sales Manager', 'Country', 'Product Type', 'Product Count', 'Amount', 'Probability', 'Created', 'Expires', 'Last Updated'];
  const rows = quotes.map(q => [q.id, getQuoteName(q), q.status, q.customer, q.dealer, getQuotedBy(q), q.businessUnit, q.salesManager, q.country, PRODUCT_TYPE_LABEL[getProductType(q)], getProductCount(q), q.amount, q.probability, q.date, getExpiresAt(q), q.lastUpdated]);
  const escape = (v: string | number) => {
    const s = String(v ?? '');
    if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');
  // BOM so Excel detects UTF-8 properly
  const blob = new Blob(['\uFEFF' + csv], {
    type: 'text/csv;charset=utf-8;'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `quotes-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
const EMPTY_FILTERS: QuoteFilters = {
  status: [],
  businessUnit: [],
  salesManager: [],
  country: [],
  dealer: [],
  probabilityBucket: [],
  quotedByMe: false
};
// Initial filters applied when the Explorer first loads (user can still Clear all)
const INITIAL_FILTERS: QuoteFilters = {
  ...EMPTY_FILTERS,
  status: ['Active', 'Expired']
};
function FilterAccordionSection({
  label,
  options,
  selected,
  onChange,
  defaultOpen = false
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const toggleOption = (opt: string) => {
    if (selected.includes(opt)) {
      onChange(selected.filter(s => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };
  // Sort: selected first, then unselected
  const sorted = [...options.filter(o => selected.includes(o)), ...options.filter(o => !selected.includes(o))];
  const summary = selected.length === 0 ? 'All' : selected.length === 1 ? selected[0] : `${selected.length} selected`;
  return <div className="border border-white/5 rounded-md bg-white/5 overflow-hidden" data-id="element-12107">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-white/5 transition-colors" data-id="element-12108">
        <div className="min-w-0 flex-1" data-id="element-12109">
          <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12110">
            {label}
          </p>
          <p className={`text-xs mt-0.5 truncate ${selected.length > 0 ? 'text-white font-medium' : 'text-blue-300/60'}`} data-id="element-12111">
            {summary}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-12112">
          {selected.length > 0 && <button onClick={e => {
          e.stopPropagation();
          onChange([]);
        }} className="text-blue-300/60 hover:text-white p-0.5 rounded hover:bg-white/10" aria-label={`Clear ${label} filter`} data-id="element-12113">
              <XIcon size={12} data-id="element-12114" />
            </button>}
          <ChevronDownIcon size={14} className={`text-blue-300/60 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-12115" />
        </div>
      </button>
      {open && <div className="border-t border-white/5 max-h-44 overflow-y-auto no-scrollbar" data-id="element-12116">
          {sorted.length === 0 ? <p className="text-xs text-blue-300/40 italic px-3 py-2" data-id="element-12117">
              No options available
            </p> : sorted.map(opt => {
        const isSelected = selected.includes(opt);
        return <button key={opt} onClick={() => toggleOption(opt)} className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/5 transition-colors" data-id="element-12118">
                  <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-blue-500 border-blue-400' : 'border-white/20 bg-white/5'}`} data-id="element-12119">
                    {isSelected && <CheckCircleIcon size={10} className="text-white" data-id="element-12120" />}
                  </div>
                  <span className={`text-xs truncate ${isSelected ? 'text-white font-medium' : 'text-blue-200'}`} data-id="element-12121">
                    {opt}
                  </span>
                </button>;
      })}
        </div>}
    </div>;
}
// --- KPI customization (Phase 1) ---
type KpiId = 'totalQuotes' | 'totalAmount' | 'statusBreakdown' | 'needsAttention';
const KPI_META: Record<KpiId, {
  label: string;
}> = {
  totalQuotes: {
    label: 'Total Quotes'
  },
  totalAmount: {
    label: 'Importe Total'
  },
  statusBreakdown: {
    label: 'Status (dynamic)'
  },
  needsAttention: {
    label: 'Needs Attention'
  }
};
const ALL_KPIS: KpiId[] = ['totalQuotes', 'totalAmount', 'statusBreakdown', 'needsAttention'];
interface KpiPrefs {
  showKpis: boolean;
  visibleKpis: KpiId[];
}
function getDefaultKpiPrefs(_role: string): KpiPrefs {
  // All roles see the 4 KPIs by default; users can still customize.
  return {
    showKpis: true,
    visibleKpis: ['totalQuotes', 'totalAmount', 'statusBreakdown', 'needsAttention']
  };
}
// --- Quote Notes Panel (chat-like internal communication) ---
interface ChatMessage {
  id: string;
  author: string;
  role: 'sales-manager' | 'dealer' | 'operations' | 'bu-manager' | 'customer';
  date: string;
  text: string;
  isChangeRequest?: boolean;
}
function getNotesThread(q: Quote): ChatMessage[] {
  const seed = hashCode(q.id);
  const sm = q.salesManager || 'Sales Manager';
  const dealer = q.dealer || 'Dealer';
  const baseDate = new Date(q.date);
  const dayOffset = (n: number) => {
    const d = new Date(baseDate);
    d.setDate(d.getDate() + n);
    return d.toISOString();
  };
  // Realistic templates rotated by seed
  const templates: ChatMessage[][] = [[{
    id: 'm1',
    author: dealer,
    role: 'dealer',
    date: dayOffset(1),
    text: 'Customer wants a 12% discount to close. Can we authorise?',
    isChangeRequest: true
  }, {
    id: 'm2',
    author: sm,
    role: 'sales-manager',
    date: dayOffset(2),
    text: 'Max we can offer is 8% given current margin. Try bundling the service plan instead — that often closes the gap.'
  }, {
    id: 'm3',
    author: dealer,
    role: 'dealer',
    date: dayOffset(3),
    text: 'Customer also asked if we can switch to 8" pipe instead of 6". Need updated quote.',
    isChangeRequest: true
  }], [{
    id: 'm1',
    author: dealer,
    role: 'dealer',
    date: dayOffset(2),
    text: 'Customer needs delivery before May 15. Is that feasible?',
    isChangeRequest: true
  }, {
    id: 'm2',
    author: 'Operations',
    role: 'operations',
    date: dayOffset(3),
    text: 'Current lead time is 6 weeks. We could expedite if customer pays 3% rush fee.'
  }, {
    id: 'm3',
    author: sm,
    role: 'sales-manager',
    date: dayOffset(4),
    text: 'Let me know if they accept the rush fee. Otherwise we ship by June 1.'
  }], [{
    id: 'm1',
    author: dealer,
    role: 'dealer',
    date: dayOffset(1),
    text: 'Customer reviewed quote and wants to add 2 more pivots to the order.',
    isChangeRequest: true
  }, {
    id: 'm2',
    author: sm,
    role: 'sales-manager',
    date: dayOffset(2),
    text: 'Great — I’ll prepare an updated quote. Bulk pricing should kick in at 5+ units.'
  }]];
  return templates[Math.abs(seed) % templates.length];
}
function roleColor(role: ChatMessage['role']) {
  switch (role) {
    case 'sales-manager':
      return 'bg-blue-500/20 text-blue-200 border-blue-400/30';
    case 'dealer':
      return 'bg-amber-500/20 text-amber-200 border-amber-400/30';
    case 'operations':
      return 'bg-indigo-500/20 text-indigo-200 border-indigo-400/30';
    case 'bu-manager':
      return 'bg-purple-500/20 text-purple-200 border-purple-400/30';
    case 'customer':
      return 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30';
  }
}
function roleLabel(role: ChatMessage['role']) {
  switch (role) {
    case 'sales-manager':
      return 'Sales Manager';
    case 'dealer':
      return 'Dealer';
    case 'operations':
      return 'Operations';
    case 'bu-manager':
      return 'BU Manager';
    case 'customer':
      return 'Customer';
  }
}
function QuoteNotesPanel({
  quote
}: {
  quote: Quote;
}) {
  const {
    name: currentUser
  } = useUser();
  const [draft, setDraft] = useState('');
  const [extra, setExtra] = useState<ChatMessage[]>([]);
  const baseThread = useMemo(() => getNotesThread(quote), [quote.id]);
  const messages = useMemo(() => [...baseThread, ...extra].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()), [baseThread, extra]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const submit = () => {
    const text = draft.trim();
    if (!text) return;
    setExtra(arr => [...arr, {
      id: `local-${Date.now()}`,
      author: currentUser || 'You',
      role: 'sales-manager',
      date: new Date().toISOString(),
      text
    }]);
    setDraft('');
  };
  // Keep the chat scrolled to the latest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);
  const initials = (name: string) => name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  const isMine = (m: ChatMessage) => m.author === (currentUser || 'You');
  return <section className="flex flex-col h-full" data-id="element-12122">
      <div className="px-4 py-2.5 border-b border-white/5 flex-shrink-0" data-id="element-12123">
        <p className="text-[11px] text-blue-300/70 leading-relaxed" data-id="element-12124">
          Internal thread between sales manager, dealer and operations. Use this
          to track change requests and decisions.
        </p>
      </div>
      {/* Chat thread */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-4" data-id="element-12125">
        {messages.length === 0 ? <p className="text-xs text-blue-300/60 italic text-center py-6" data-id="element-12126">
            No messages yet — start the conversation below.
          </p> : messages.map(m => {
        const mine = isMine(m);
        return <div key={m.id} className={`flex items-end gap-2 ${mine ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-12127">
                {/* Avatar */}
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${mine ? 'bg-blue-500/30 text-blue-100' : 'bg-white/10 text-blue-200'}`} title={m.author} data-id="element-12128">
                  {initials(m.author)}
                </div>
                {/* Bubble + meta */}
                <div className={`flex flex-col max-w-[78%] min-w-0 ${mine ? 'items-end' : 'items-start'}`} data-id="element-12129">
                  <div className={`flex items-center gap-1.5 mb-1 flex-wrap ${mine ? 'flex-row-reverse' : ''}`} data-id="element-12130">
                    <span className="text-[11px] font-medium text-white truncate" data-id="element-12131">
                      {mine ? 'You' : m.author}
                    </span>
                    <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${roleColor(m.role)}`} data-id="element-12132">
                      {roleLabel(m.role)}
                    </span>
                    {m.isChangeRequest && <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-400/30" data-id="element-12133">
                        Change request
                      </span>}
                  </div>
                  <div className={`px-3 py-2 text-sm leading-relaxed break-words ${mine ? 'bg-blue-600 text-white rounded-2xl rounded-br-sm' : 'bg-white/10 text-blue-100 border border-white/10 rounded-2xl rounded-bl-sm'}`} data-id="element-12134">
                    {m.text}
                  </div>
                  <span className="text-[10px] text-blue-300/50 tabular-nums mt-1 px-1" data-id="element-12135">
                    {formatLongDate(m.date)}
                  </span>
                </div>
              </div>;
      })}
      </div>
      {/* Composer */}
      <div className="px-3 py-3 border-t border-white/10 flex-shrink-0 bg-slate-900" data-id="element-12136">
        <div className="flex items-end gap-2 bg-white/5 border border-white/10 rounded-2xl px-3 py-2 focus-within:ring-1 focus-within:ring-blue-400 transition-shadow" data-id="element-12137">
          <textarea value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }} placeholder="Write a message…" rows={1} className="flex-1 resize-none bg-transparent text-sm text-white placeholder-blue-300/50 focus:outline-none max-h-28 no-scrollbar leading-relaxed" data-id="element-12138" />
          <button onClick={submit} disabled={!draft.trim()} className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 hover:bg-blue-700 disabled:bg-white/5 disabled:text-blue-300/30 text-white transition-colors" aria-label="Send message" title="Send (Enter)" data-id="element-12139">
            <SendIcon size={14} data-id="element-12140" />
          </button>
        </div>
        <p className="text-[10px] text-blue-300/40 mt-1.5 px-1" data-id="element-12141">
          Press Enter to send · Shift+Enter for a new line
        </p>
      </div>
    </section>;
}
// --- Quote Detail Drawer ---
function QuoteDetailDrawer({
  quote,
  mode,
  onModeChange,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  position,
  total
}: {
  quote: Quote | null;
  mode: 'summary' | 'timeline' | 'notes';
  onModeChange: (m: 'summary' | 'timeline' | 'notes') => void;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  position: number;
  total: number;
}) {
  useEffect(() => {
    if (!quote) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [quote, hasPrev, hasNext, onClose, onPrev, onNext]);
  if (!quote) return null;
  const closureNote = getClosureNote(quote);
  const timeline = getTimeline(quote);
  const expiresAt = getExpiresAt(quote);
  const productType = getProductType(quote);
  const productCount = getProductCount(quote);
  const isClosed = ['Lost', 'Cancelled', 'Won', 'Expired'].includes(quote.status);
  const closureBorder = quote.status === 'Lost' || quote.status === 'Cancelled' ? 'border-rose-500/30 bg-rose-500/5' : quote.status === 'Won' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-blue-500/30 bg-blue-500/5';
  const closureIcon = quote.status === 'Lost' || quote.status === 'Cancelled' ? 'text-rose-300' : quote.status === 'Won' ? 'text-emerald-300' : 'text-blue-300';
  const timelineDotColor = (kind: ActivityKind, status?: QuoteStatus) => {
    if (kind === 'status-change') {
      if (status === 'Won') return 'bg-emerald-400';
      if (status === 'Lost' || status === 'Cancelled') return 'bg-rose-400';
      if (status === 'Expired') return 'bg-amber-400';
    }
    if (kind === 'note') return 'bg-blue-400';
    if (kind === 'discount') return 'bg-amber-400';
    if (kind === 'items-changed') return 'bg-indigo-400';
    if (kind === 'created') return 'bg-blue-500';
    return 'bg-blue-400/50';
  };
  return <>
      {/* No backdrop — table stays fully interactive while drawer is open.
                        Closing happens via X, Esc, or selecting another quote. */}
      {/* Drawer */}
      <aside role="dialog" aria-label={`Quote ${quote.id} details`} className="fixed top-0 right-0 z-50 h-full w-full sm:w-[460px] bg-slate-900 border-l border-white/10 shadow-2xl flex flex-col" data-id="element-12142">
        {/* Header */}
        <header className="flex items-center gap-2 px-4 py-3 border-b border-white/10 flex-shrink-0" data-id="element-12143">
          <div className="flex-1 min-w-0" data-id="element-12144">
            <div className="flex items-center gap-2" data-id="element-12145">
              <span className="text-[11px] font-mono text-blue-400" data-id="element-12146">
                {quote.id}
              </span>
              <StatusBadge status={quote.status} data-id="element-12147" />
            </div>
            <p className="text-sm text-white font-medium truncate mt-0.5" data-id="element-12148">
              {getQuoteName(quote)}
            </p>
          </div>
          {total > 1 && <div className="flex items-center gap-0.5 mr-1" data-id="element-12149">
              <button onClick={onPrev} disabled={!hasPrev} aria-label="Previous quote" className="w-7 h-7 flex items-center justify-center rounded text-blue-200 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Previous (←)" data-id="element-12150">
                <ChevronLeftIcon size={14} data-id="element-12151" />
              </button>
              <span className="text-[10px] text-blue-300/60 tabular-nums px-1 min-w-[2.5rem] text-center" data-id="element-12152">
                {position + 1} / {total}
              </span>
              <button onClick={onNext} disabled={!hasNext} aria-label="Next quote" className="w-7 h-7 flex items-center justify-center rounded text-blue-200 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Next (→)" data-id="element-12153">
                <ChevronRightIcon size={14} data-id="element-12154" />
              </button>
            </div>}
          <button onClick={onClose} aria-label="Close" className="w-7 h-7 flex items-center justify-center rounded text-blue-200 hover:text-white hover:bg-white/10 transition-colors" title="Close (Esc)" data-id="element-12155">
            <XIcon size={16} data-id="element-12156" />
          </button>
        </header>
        {/* Tabs */}
        <div className="flex items-center px-2 border-b border-white/10 flex-shrink-0 bg-slate-900" data-id="element-12157">
          {[{
          id: 'summary' as const,
          label: 'Summary',
          icon: <FileTextIcon size={12} data-id="element-12158" />
        }, {
          id: 'timeline' as const,
          label: 'Timeline',
          icon: <ClockIcon size={12} data-id="element-12159" />
        }, {
          id: 'notes' as const,
          label: 'Notes',
          icon: <MessageSquareIcon size={12} data-id="element-12160" />
        }].map(t => {
          const isActive = mode === t.id;
          return <button key={t.id} onClick={() => onModeChange(t.id as any)} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${isActive ? 'border-blue-400 text-white' : 'border-transparent text-blue-300/70 hover:text-white'}`} data-id="element-12161">
                {t.icon}
                {t.label}
              </button>;
        })}
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto no-scrollbar" data-id="element-12162">
          {mode === 'summary' && <section className="px-4 py-3" data-id="element-12163">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold mb-3" data-id="element-12164">
                Summary
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-12165">
                <div data-id="element-12166">
                  <p className="text-blue-300/60 mb-1" data-id="element-12167">Amount</p>
                  <p className="text-white font-semibold tabular-nums text-lg" data-id="element-12168">
                    ${quote.amount.toLocaleString()}
                  </p>
                </div>
                <div data-id="element-12169">
                  <p className="text-blue-300/60 mb-1" data-id="element-12170">Products</p>
                  <p className="text-white font-medium text-lg" data-id="element-12171">
                    {productCount}{' '}
                    <span className="text-blue-300/70 text-base" data-id="element-12172">
                      {PRODUCT_TYPE_LABEL[productType]}
                    </span>
                  </p>
                </div>
                <div data-id="element-12173">
                  <p className="text-blue-300/60 mb-1" data-id="element-12174">Dealer</p>
                  <p className="text-white" data-id="element-12175">{quote.dealer || '—'}</p>
                </div>
                <div data-id="element-12176">
                  <p className="text-blue-300/60 mb-1" data-id="element-12177">Customer</p>
                  <p className="text-white" data-id="element-12178">{quote.customer}</p>
                </div>
                <div data-id="element-12179">
                  <p className="text-blue-300/60 mb-1" data-id="element-12180">Quoted by</p>
                  <p className="text-white" data-id="element-12181">{getQuotedBy(quote)}</p>
                </div>
                <div data-id="element-12182">
                  <p className="text-blue-300/60 mb-1" data-id="element-12183">Country</p>
                  <p className="text-white" data-id="element-12184">{quote.country}</p>
                </div>
                <div data-id="element-12185">
                  <p className="text-blue-300/60 mb-1" data-id="element-12186">Created</p>
                  <p className="text-white tabular-nums" data-id="element-12187">
                    {formatLongDate(quote.date)}
                  </p>
                </div>
                <div data-id="element-12188">
                  <p className="text-blue-300/60 mb-1" data-id="element-12189">Expires</p>
                  <p className="text-white tabular-nums" data-id="element-12190">
                    {formatLongDate(expiresAt)}
                  </p>
                </div>
                <div data-id="element-12191">
                  <p className="text-blue-300/60 mb-1" data-id="element-12192">Sale probability</p>
                  <span className={`inline-block text-sm font-semibold px-2 py-0.5 rounded border tabular-nums ${quote.probability > 70 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : quote.probability >= 30 ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' : 'bg-rose-500/20 text-rose-300 border-rose-500/30'}`} data-id="element-12193">
                    {quote.probability}%
                  </span>
                </div>
              </div>
            </section>}
          {mode === 'timeline' && <>
              {/* Closure note */}
              {closureNote && <section className="px-4 py-3 border-b border-white/5" data-id="element-12194">
                  <div className="flex items-center gap-1.5 mb-2" data-id="element-12195">
                    <FileTextIcon size={12} className={closureIcon} data-id="element-12196" />
                    <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12197">
                      {quote.status === 'Won' ? 'Win note' : 'Closure note'}
                    </p>
                  </div>
                  <div className={`rounded-md border px-3 py-2.5 ${closureBorder}`} data-id="element-12198">
                    <p className="text-sm text-white leading-relaxed" data-id="element-12199">
                      {closureNote.text}
                    </p>
                    <p className="text-[11px] text-blue-300/60 mt-2" data-id="element-12200">
                      — {closureNote.author}, {formatLongDate(closureNote.date)}
                    </p>
                  </div>
                </section>}
              {/* Activity timeline */}
              <section className="px-4 py-3" data-id="element-12201">
                <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold mb-3" data-id="element-12202">
                  Activity timeline
                </p>
                <ol className="relative space-y-3" data-id="element-12203">
                  {timeline.map((ev, i) => {
                const isLast = i === timeline.length - 1;
                return <li key={`${ev.date}-${i}`} className="flex gap-3" data-id="element-12204">
                        <div className="flex flex-col items-center flex-shrink-0" data-id="element-12205">
                          <span className={`w-2.5 h-2.5 rounded-full ${timelineDotColor(ev.kind, ev.toStatus)} ring-2 ring-slate-900`} data-id="element-12206" />
                          {!isLast && <span className="w-px flex-1 bg-white/10 mt-1" data-id="element-12207" />}
                        </div>
                        <div className="flex-1 min-w-0 pb-1" data-id="element-12208">
                          <div className="flex items-baseline justify-between gap-2" data-id="element-12209">
                            <p className="text-xs text-white font-medium" data-id="element-12210">
                              {ev.title}
                            </p>
                            <p className="text-[10px] text-blue-300/60 tabular-nums flex-shrink-0" data-id="element-12211">
                              {formatLongDate(ev.date)}
                            </p>
                          </div>
                          {ev.detail && <p className="text-[11px] text-blue-200/80 mt-1 leading-relaxed" data-id="element-12212">
                              {ev.detail}
                            </p>}
                          {ev.author && <p className="text-[10px] text-blue-300/50 mt-0.5" data-id="element-12213">
                              by {ev.author}
                            </p>}
                        </div>
                      </li>;
              })}
                </ol>
              </section>
            </>}
          {mode === 'notes' && <QuoteNotesPanel quote={quote} data-id="element-12214" />}
        </div>
        {/* Footer */}
        <footer className="px-4 py-3 border-t border-white/10 flex-shrink-0" data-id="element-12215">
          <button type="button" className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium py-2 rounded-md transition-colors" data-id="element-12216">
            Open full quote
            <ChevronRightIcon size={14} data-id="element-12217" />
          </button>
        </footer>
      </aside>
    </>;
}
function AllQuotesExplorerView({
  role,
  configOpen,
  setConfigOpen
}: {
  role: string;
  configOpen: boolean;
  setConfigOpen: (open: boolean) => void;
}) {
  const dummyQuotes = useQuotes();
  const {
    name: userName
  } = useUser();
  const [chain, setChain] = useState<GroupDimension[]>(() => getDefaultChain(role));
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [chainPresetOpen, setChainPresetOpen] = useState(false);
  const [sort, setSort] = useState<{
    key: SortKey;
    dir: SortDir;
  }>({
    key: 'date',
    dir: 'desc'
  });
  const [sortOpen, setSortOpen] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [drawerMode, setDrawerMode] = useState<'summary' | 'timeline' | 'notes'>('summary');
  const handleQuoteAction = (id: string, action: 'view' | 'timeline' | 'notes') => {
    if (action === 'view') {
      // Placeholder — would route to the full quote page in production.
      // For prototype, open the drawer at timeline mode as a graceful fallback.
      setSelectedQuoteId(id);
      setDrawerMode('summary');
      return;
    }
    setSelectedQuoteId(id);
    setDrawerMode(action);
  };
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [filters, setFilters] = useState<QuoteFilters>(INITIAL_FILTERS);
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  // Available years from data (newest first)
  const availableYears = useMemo(() => {
    const ys = new Set<number>();
    dummyQuotes.forEach(q => {
      const y = new Date(q.date).getFullYear();
      if (!isNaN(y)) ys.add(y);
    });
    return Array.from(ys).sort((a, b) => b - a);
  }, [dummyQuotes]);
  // KPI preferences (persisted per role in localStorage)
  const kpiStorageKey = `quoteExplorer:kpiPrefs:${role}`;
  const [kpiPrefs, setKpiPrefs] = useState<KpiPrefs>(() => {
    if (typeof window === 'undefined') return getDefaultKpiPrefs(role);
    try {
      const raw = window.localStorage.getItem(kpiStorageKey);
      if (!raw) return getDefaultKpiPrefs(role);
      const parsed = JSON.parse(raw) as KpiPrefs;
      // Validate
      if (typeof parsed.showKpis === 'boolean' && Array.isArray(parsed.visibleKpis)) {
        const valid = parsed.visibleKpis.filter(k => ALL_KPIS.includes(k));
        // If the stored prefs predate a schema change and ALL ids are stale,
        // fall back to defaults so the user isn't left with an empty strip.
        if (valid.length === 0 && parsed.visibleKpis.length > 0) {
          return getDefaultKpiPrefs(role);
        }
        return {
          showKpis: parsed.showKpis,
          visibleKpis: valid
        };
      }
      return getDefaultKpiPrefs(role);
    } catch {
      return getDefaultKpiPrefs(role);
    }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(kpiStorageKey, JSON.stringify(kpiPrefs));
    } catch {
      /* ignore quota errors */
    }
  }, [kpiPrefs, kpiStorageKey]);
  const toggleKpi = (id: KpiId) => setKpiPrefs(p => ({
    ...p,
    visibleKpis: p.visibleKpis.includes(id) ? p.visibleKpis.filter(k => k !== id) : [...p.visibleKpis, id]
  }));
  const resetKpiPrefs = () => setKpiPrefs(getDefaultKpiPrefs(role));
  const addKpi = (id: KpiId) => setKpiPrefs(p => p.visibleKpis.includes(id) ? p : {
    ...p,
    visibleKpis: [...p.visibleKpis, id]
  });
  // Drag-and-drop for KPI panel order
  const [kpiDragIndex, setKpiDragIndex] = useState<number | null>(null);
  const [kpiDropIndex, setKpiDropIndex] = useState<number | null>(null);
  const handleKpiDragStart = (i: number) => setKpiDragIndex(i);
  const handleKpiDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (kpiDragIndex === null || kpiDragIndex === i) return;
    setKpiDropIndex(i);
  };
  const handleKpiDrop = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (kpiDragIndex === null || kpiDragIndex === i) {
      setKpiDragIndex(null);
      setKpiDropIndex(null);
      return;
    }
    setKpiPrefs(p => {
      const next = [...p.visibleKpis];
      const [moved] = next.splice(kpiDragIndex, 1);
      next.splice(i, 0, moved);
      return {
        ...p,
        visibleKpis: next
      };
    });
    setKpiDragIndex(null);
    setKpiDropIndex(null);
  };
  const handleKpiDragEnd = () => {
    setKpiDragIndex(null);
    setKpiDropIndex(null);
  };
  // Drag-and-drop handlers for Group by chain
  const handleDragStart = (i: number) => setDragIndex(i);
  const handleDragOver = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) return;
    setDropIndex(i);
  };
  const handleDragLeave = () => {
    // keep dropIndex until drop or dragend to avoid flicker
  };
  const handleDrop = (e: React.DragEvent, i: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) {
      setDragIndex(null);
      setDropIndex(null);
      return;
    }
    setChain(c => {
      const next = [...c];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDragIndex(null);
    setDropIndex(null);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setDropIndex(null);
  };
  // Filter option lists (unique sorted values from dataset)
  const filterOptions = useMemo(() => {
    const uniqueSorted = (vals: string[]) => Array.from(new Set(vals)).sort((a, b) => a.localeCompare(b));
    return {
      status: ['Draft', 'Active', 'Expired', 'Won', 'Lost', 'Cancelled'] as QuoteStatus[],
      businessUnit: uniqueSorted(dummyQuotes.map(q => q.businessUnit)),
      salesManager: uniqueSorted(dummyQuotes.map(q => q.salesManager)),
      country: uniqueSorted(dummyQuotes.map(q => q.country)),
      dealer: uniqueSorted(dummyQuotes.map(q => q.dealer))
    };
  }, [dummyQuotes]);
  const activeFilterCount = filters.status.length + filters.businessUnit.length + filters.salesManager.length + filters.country.length + filters.dealer.length + filters.probabilityBucket.length + (filters.quotedByMe ? 1 : 0);
  const clearAllFilters = () => setFilters(EMPTY_FILTERS);
  // Filter quotes by date range, filters AND search
  const filteredQuotes = useMemo(() => {
    const probBucket = (p: number): 'high' | 'medium' | 'low' => p > 70 ? 'high' : p >= 30 ? 'medium' : 'low';
    let base = dummyQuotes.filter(q => {
      if (!quoteMatchesDateRange(q, dateRange)) return false;
      if (filters.quotedByMe && getQuotedBy(q) !== userName) return false;
      if (filters.status.length > 0 && !filters.status.includes(q.status)) return false;
      if (filters.businessUnit.length > 0 && !filters.businessUnit.includes(q.businessUnit)) return false;
      if (filters.salesManager.length > 0 && !filters.salesManager.includes(q.salesManager)) return false;
      if (filters.country.length > 0 && !filters.country.includes(q.country)) return false;
      if (filters.dealer.length > 0 && !filters.dealer.includes(q.dealer)) return false;
      if (filters.probabilityBucket.length > 0 && !filters.probabilityBucket.includes(probBucket(q.probability))) return false;
      return true;
    });
    const sQ = search.trim().toLowerCase();
    if (sQ) {
      base = base.filter(qu => qu.customer.toLowerCase().includes(sQ) || qu.dealer.toLowerCase().includes(sQ) || qu.salesManager.toLowerCase().includes(sQ) || qu.businessUnit.toLowerCase().includes(sQ) || qu.country.toLowerCase().includes(sQ) || qu.id.toLowerCase().includes(sQ));
    }
    return base;
  }, [search, filters, dateRange, userName]);
  // Sort applied to leaf quotes (groups stay sorted by openAmount desc)
  const sortedFilteredQuotes = useMemo(() => sortQuotes(filteredQuotes, sort), [filteredQuotes, sort]);
  // Build tree based on chain
  const tree = useMemo(() => buildTree(sortedFilteredQuotes, chain, 0, ''), [sortedFilteredQuotes, chain]);
  // Top-level aggregates
  const totals = useMemo(() => computeAggregates(filteredQuotes), [filteredQuotes]);
  // Statuses to display as columns in the tree (and in the KPI breakdown).
  // Mirrors the active status filter; falls back to Active+Expired when nothing is filtered.
  const statusesToShow: QuoteStatus[] = useMemo(() => filters.status.length > 0 ? filters.status : ['Active', 'Expired'], [filters.status]);
  const toggleExpanded = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);else next.add(key);
      return next;
    });
  };
  const expandAll = () => {
    const collect = (groups: TreeGroup[]): string[] => {
      const out: string[] = [];
      groups.forEach(g => {
        out.push(g.key);
        out.push(...collect(g.children));
      });
      return out;
    };
    setExpanded(new Set(collect(tree)));
  };
  const collapseAll = () => setExpanded(new Set());
  const available = (Object.keys(DIMENSION_META) as GroupDimension[]).filter(d => !chain.includes(d));
  const kpisNotVisible = ALL_KPIS.filter(id => !kpiPrefs.visibleKpis.includes(id));
  // Navigation between quotes in the drawer uses the current sorted/filtered list
  const navQuotes = sortedFilteredQuotes;
  const selectedIndex = selectedQuoteId ? navQuotes.findIndex(q => q.id === selectedQuoteId) : -1;
  const selectedQuote = selectedIndex >= 0 ? navQuotes[selectedIndex] : null;
  return <>
      {/* Quote detail drawer */}
      {selectedQuote && <QuoteDetailDrawer quote={selectedQuote} mode={drawerMode} onModeChange={setDrawerMode} onClose={() => setSelectedQuoteId(null)} onPrev={() => {
      if (selectedIndex > 0) setSelectedQuoteId(navQuotes[selectedIndex - 1].id);
    }} onNext={() => {
      if (selectedIndex >= 0 && selectedIndex < navQuotes.length - 1) setSelectedQuoteId(navQuotes[selectedIndex + 1].id);
    }} hasPrev={selectedIndex > 0} hasNext={selectedIndex >= 0 && selectedIndex < navQuotes.length - 1} position={selectedIndex} total={navQuotes.length} data-id="element-12218" />}
      {/* Main content — single column, no flex sibling */}
      <div className="space-y-6" data-id="element-12219">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-12220">
          <div className="relative flex-1 max-w-md" data-id="element-12221">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300/50" data-id="element-12222" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search quotes..." className="bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full" data-id="element-12223" />
          </div>
          <div className="flex items-center gap-2" data-id="element-12224">
            {/* Export to Excel */}
            <button onClick={() => exportQuotesToCsv(sortedFilteredQuotes)} className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-blue-100 px-3 py-2 rounded-lg text-sm font-medium transition-colors" title={`Export ${sortedFilteredQuotes.length} quotes to Excel`} data-id="element-12225">
              <DownloadIcon size={14} data-id="element-12226" />
              <span className="hidden md:inline" data-id="element-12227">Export</span>
            </button>
            <button onClick={() => setConfigOpen(!configOpen)} aria-pressed={configOpen} aria-label="Toggle configuration panel" className={`relative flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${configOpen ? 'bg-blue-600 border-blue-500 text-white hover:bg-blue-700' : 'bg-white/5 border-white/10 text-blue-300 hover:bg-white/10 hover:text-white'}`} title="Toggle Configuration" data-id="element-12228">
              <Settings2Icon size={16} data-id="element-12229" />
              {activeFilterCount > 0 && <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[9px] font-bold px-1 min-w-[14px] h-[14px] rounded-full flex items-center justify-center" data-id="element-12230">
                  {activeFilterCount}
                </span>}
            </button>
            <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-12231">
              <PlusIcon size={16} data-id="element-12232" />
              <span className="hidden sm:inline" data-id="element-12233">New Quote</span>
            </button>
          </div>
        </div>

        {/* KPI strip (configurable per user) */}
        {kpiPrefs.showKpis && kpiPrefs.visibleKpis.length > 0 && <div className={`grid gap-4 ${kpiPrefs.visibleKpis.length === 1 ? 'grid-cols-1' : kpiPrefs.visibleKpis.length === 2 ? 'grid-cols-1 md:grid-cols-2' : kpiPrefs.visibleKpis.length === 3 ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-4'}`} data-id="element-12234">
            {kpiPrefs.visibleKpis.map(id => {
          if (id === 'totalQuotes') {
            return <KPICard key={id} title="Total Quotes" value={filteredQuotes.length} icon={<ListIcon size={20} data-id="element-12236" />} data-id="element-12235" />;
          }
          if (id === 'totalAmount') {
            return <KPICard key={id} title="Importe Total" value={`${totals.totalAmount.toLocaleString()}`} icon={<DollarSignIcon size={20} data-id="element-12238" />} data-id="element-12237" />;
          }
          if (id === 'statusBreakdown') {
            const statusColors = STATUS_DOT_COLORS;
            const breakdown = statusesToShow.map(s => {
              const matching = filteredQuotes.filter(q => q.status === s);
              return {
                status: s,
                count: matching.length,
                amount: matching.reduce((acc, q) => acc + q.amount, 0)
              };
            });
            return <div key={id} className="bg-white/5 border border-white/10 rounded-xl p-4 backdrop-blur-sm flex flex-col" data-id="element-12239">
                    <div className="flex items-center justify-between mb-3" data-id="element-12240">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12241">
                        By Status
                      </p>
                      <TrendingUpIcon size={20} className="text-blue-400" data-id="element-12242" />
                    </div>
                    <div className="space-y-2 flex-1" data-id="element-12243">
                      {breakdown.map(b => <div key={b.status} className="flex items-center justify-between gap-3" data-id="element-12244">
                          <div className="flex items-center gap-2 min-w-0" data-id="element-12245">
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusColors[b.status]}`} data-id="element-12246" />
                            <span className="text-xs text-blue-100 font-medium truncate" data-id="element-12247">
                              {b.status}
                            </span>
                          </div>
                          <div className="flex items-baseline gap-2 flex-shrink-0" data-id="element-12248">
                            <span className="text-sm font-bold text-white tabular-nums" data-id="element-12249">
                              {b.count}
                            </span>
                            <span className="text-[11px] text-blue-300/70 tabular-nums" data-id="element-12250">
                              ${b.amount.toLocaleString()}
                            </span>
                          </div>
                        </div>)}
                    </div>
                  </div>;
          }
          if (id === 'needsAttention') {
            return <KPICard key={id} title="Needs Attention" value={totals.needsAttention} icon={<AlertCircleIcon size={20} data-id="element-12252" />} data-id="element-12251" />;
          }
          return null;
        })}
          </div>}

        {/* Active filters strip */}
        {(activeFilterCount > 0 || !isDefaultDateRange(dateRange)) && <div className="flex items-center gap-2 flex-wrap bg-white/5 border border-amber-500/20 rounded-lg px-3 py-2 backdrop-blur-sm" data-id="element-12253">
            <SlidersHorizontalIcon size={14} className="text-amber-400 flex-shrink-0" data-id="element-12254" />
            <span className="text-xs text-blue-200 font-medium flex-shrink-0" data-id="element-12255">
              {filteredQuotes.length} of {dummyQuotes.length} quotes
            </span>
            <span className="text-blue-300/30" data-id="element-12256">·</span>
            {!isDefaultDateRange(dateRange) && <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-100 text-xs" data-id="element-12257">
                <CalendarIcon size={11} className="text-amber-300" data-id="element-12258" />
                <span className="font-medium" data-id="element-12259">
                  {formatDateRangeLabel(dateRange)}
                </span>
                <button onClick={() => setDateRange(DEFAULT_DATE_RANGE)} className="text-amber-300/70 hover:text-white rounded-full hover:bg-amber-500/20 w-4 h-4 flex items-center justify-center -mr-0.5" aria-label="Reset date range" data-id="element-12260">
                  <XIcon size={11} data-id="element-12261" />
                </button>
              </div>}
            {filters.quotedByMe && <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-blue-500/15 border border-blue-400/40 text-blue-100 text-xs" data-id="element-12262">
                <UserIcon size={11} className="text-blue-300" data-id="element-12263" />
                <span className="font-medium" data-id="element-12264">Quoted by me</span>
                <button onClick={() => setFilters(f => ({
            ...f,
            quotedByMe: false
          }))} className="text-blue-300/70 hover:text-white rounded-full hover:bg-blue-500/20 w-4 h-4 flex items-center justify-center -mr-0.5" aria-label="Clear Quoted by me" data-id="element-12265">
                  <XIcon size={11} data-id="element-12266" />
                </button>
              </div>}
            {([{
          key: 'status' as const,
          label: 'Status',
          values: filters.status
        }, {
          key: 'businessUnit' as const,
          label: 'BU',
          values: filters.businessUnit
        }, {
          key: 'salesManager' as const,
          label: 'Sales Mgr',
          values: filters.salesManager
        }, {
          key: 'country' as const,
          label: 'Country',
          values: filters.country
        }, {
          key: 'dealer' as const,
          label: 'Dealer',
          values: filters.dealer
        }, {
          key: 'probabilityBucket' as const,
          label: 'Probability',
          values: filters.probabilityBucket.map(b => b === 'high' ? 'High' : b === 'medium' ? 'Medium' : 'Low')
        }] as const).filter(cat => cat.values.length > 0).map(cat => <div key={cat.key} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-100 text-xs" data-id="element-12267">
                  <span className="text-amber-300/70 font-medium" data-id="element-12268">
                    {cat.label}:
                  </span>
                  <span className="font-medium truncate max-w-[180px]" data-id="element-12269">
                    {cat.values.length <= 2 ? cat.values.join(', ') : `${cat.values.length} selected`}
                  </span>
                  <button onClick={() => setFilters(f => ({
            ...f,
            [cat.key]: []
          }))} className="text-amber-300/70 hover:text-white rounded-full hover:bg-amber-500/20 w-4 h-4 flex items-center justify-center -mr-0.5" aria-label={`Clear ${cat.label} filter`} data-id="element-12270">
                    <XIcon size={11} data-id="element-12271" />
                  </button>
                </div>)}
            <button onClick={clearAllFilters} className="ml-auto text-xs text-blue-300 hover:text-white underline underline-offset-2 decoration-blue-400/30 hover:decoration-white/60 px-2 py-0.5 rounded transition-colors flex-shrink-0" data-id="element-12272">
              Clear all
            </button>
          </div>}

        {/* Tree */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden backdrop-blur-sm" data-id="element-12273">
          {chain.length === 0 ? <div data-id="element-12274">
              {filteredQuotes.length === 0 ? <div className="py-12 text-center text-sm text-blue-300/60" data-id="element-12275">
                  No quotes match your search.
                </div> : filteredQuotes.map(q => <QuoteLeafRow key={q.id} quote={q} depth={0} onOpen={id => {
            setSelectedQuoteId(id);
            setDrawerMode('timeline');
          }} onAction={handleQuoteAction} isSelected={q.id === selectedQuoteId} data-id="element-12276" />)}
            </div> : tree.length === 0 ? <div className="py-12 text-center text-sm text-blue-300/60" data-id="element-12277">
              No quotes match your search.
            </div> : <div data-id="element-12278">
              {tree.map(node => <TreeRow key={node.key} node={node} depth={0} expanded={expanded} onToggle={toggleExpanded} isLast={false} statusesToShow={statusesToShow} onOpenQuote={id => {
            setSelectedQuoteId(id);
            setDrawerMode('timeline');
          }} onQuoteAction={handleQuoteAction} selectedQuoteId={selectedQuoteId} data-id="element-12279" />)}
            </div>}
        </div>
      </div>

      {/* Right config sidebar — page-level fixed positioning */}
      {configOpen ? <aside className="fixed top-[72px] bottom-0 right-0 z-30 w-72 bg-slate-900/95 backdrop-blur-md border-l border-white/10 flex flex-col shadow-2xl" data-id="element-12280">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/20 flex-shrink-0" data-id="element-12281">
            <div className="flex items-center gap-2" data-id="element-12282">
              <Settings2Icon size={16} className="text-blue-400" data-id="element-12283" />
              <h3 className="text-sm font-semibold text-white" data-id="element-12284">
                Configuration
              </h3>
              {activeFilterCount > 0 && <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-id="element-12285">
                  {activeFilterCount}
                </span>}
            </div>
            <button onClick={() => setConfigOpen(false)} className="text-blue-300/70 hover:text-white p-1 rounded hover:bg-white/10 transition-colors" aria-label="Collapse configuration panel" data-id="element-12286">
              <PanelRightCloseIcon size={16} data-id="element-12287" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-5 no-scrollbar" data-id="element-12288">
            {/* Date range section */}
            <div data-id="element-12289">
              <div className="flex items-center justify-between mb-2" data-id="element-12290">
                <div className="flex items-center gap-1.5" data-id="element-12291">
                  <CalendarIcon size={12} className="text-blue-400/70" data-id="element-12292" />
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12293">
                    Date range
                  </p>
                </div>
                {!isDefaultDateRange(dateRange) && <button onClick={() => setDateRange(DEFAULT_DATE_RANGE)} className="text-[10px] text-blue-300 hover:text-white px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors" data-id="element-12294">
                    Reset
                  </button>}
              </div>
              <div className="border border-white/5 rounded-md bg-white/5 px-3 py-2 space-y-2" data-id="element-12295">
                {/* Mode pills */}
                <div className="flex gap-1" data-id="element-12296">
                  {([{
                id: 'last12m',
                label: 'Last 12m'
              }, {
                id: 'year',
                label: 'Year'
              }, {
                id: 'month',
                label: 'Month'
              }] as {
                id: DateRangeMode;
                label: string;
              }[]).map(m => {
                const isOn = dateRange.mode === m.id;
                return <button key={m.id} onClick={() => setDateRange(prev => {
                  if (m.id === 'last12m') return {
                    mode: 'last12m',
                    year: new Date().getFullYear()
                  };
                  if (m.id === 'year') return {
                    mode: 'year',
                    year: prev.year ?? availableYears[0] ?? new Date().getFullYear()
                  };
                  return {
                    mode: 'month',
                    year: prev.year ?? availableYears[0] ?? new Date().getFullYear(),
                    month: prev.month ?? new Date().getMonth() + 1
                  };
                })} className={`flex-1 px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider border transition-colors ${isOn ? 'bg-blue-500/30 border-blue-400/60 text-white' : 'bg-white/5 border-white/10 text-blue-300 hover:text-white hover:bg-white/10'}`} data-id="element-12297">
                        {m.label}
                      </button>;
              })}
                </div>
                {/* Year + Month selectors */}
                {dateRange.mode !== 'last12m' && <div className="flex gap-1" data-id="element-12298">
                    <select value={dateRange.year} onChange={e => setDateRange(prev => ({
                ...prev,
                year: Number(e.target.value)
              }))} className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-400" data-id="element-12299">
                      {availableYears.length === 0 ? [<option key={new Date().getFullYear()} value={new Date().getFullYear()} data-id="element-12300">
                              {new Date().getFullYear()}
                            </option>] : availableYears.map(y => <option key={y} value={y} data-id="element-12301">
                              {y}
                            </option>)}
                    </select>
                    {dateRange.mode === 'month' && <select value={dateRange.month ?? 1} onChange={e => setDateRange(prev => ({
                ...prev,
                month: Number(e.target.value)
              }))} className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-400" data-id="element-12302">
                        {MONTH_NAMES.map((m, idx) => <option key={m} value={idx + 1} data-id="element-12303">
                            {m}
                          </option>)}
                      </select>}
                  </div>}
                {/* Active range label */}
                <p className="text-[10px] text-blue-300/60 pt-0.5" data-id="element-12304">
                  Showing: {formatDateRangeLabel(dateRange)}
                </p>
              </div>
            </div>
            {/* Filters section */}
            <div data-id="element-12305">
              <div className="flex items-center justify-between mb-2" data-id="element-12306">
                <div className="flex items-center gap-1.5" data-id="element-12307">
                  <SlidersHorizontalIcon size={12} className="text-blue-400/70" data-id="element-12308" />
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12309">
                    Filters
                  </p>
                  {activeFilterCount > 0 && <span className="bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px] font-bold px-1.5 py-0 rounded-full" data-id="element-12310">
                      {activeFilterCount}
                    </span>}
                </div>
                {activeFilterCount > 0 && <button onClick={clearAllFilters} className="text-[10px] text-blue-300 hover:text-white px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors" data-id="element-12311">
                    Clear all
                  </button>}
              </div>
              <div className="space-y-2" data-id="element-12312">
                {/* Status — inline toggle pills */}
                <div className="border border-white/5 rounded-md bg-white/5 px-3 py-2" data-id="element-12313">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold mb-1.5" data-id="element-12314">
                    Status
                  </p>
                  <div className="flex flex-wrap gap-1" data-id="element-12315">
                    {filterOptions.status.map(s => {
                  const isOn = filters.status.includes(s);
                  return <button key={s} onClick={() => setFilters(f => ({
                    ...f,
                    status: isOn ? f.status.filter(x => x !== s) : [...f.status, s]
                  }))} className={`px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider border transition-colors ${isOn ? 'bg-blue-500/30 border-blue-400/60 text-white' : 'bg-white/5 border-white/10 text-blue-300 hover:text-white hover:bg-white/10'}`} data-id="element-12316">
                          {s}
                        </button>;
                })}
                  </div>
                </div>
                {/* Group by — moved inline here, between Status and entity filters */}
                <div className="border border-white/5 rounded-md bg-white/5 px-3 py-2" data-id="element-12317">
                  <div className="flex items-center justify-between mb-2" data-id="element-12318">
                    <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12319">
                      Group by
                    </p>
                    <span className="text-[10px] text-blue-300/50" data-id="element-12320">
                      Drag to reorder
                    </span>
                  </div>
                  {chain.length === 0 ? <p className="text-xs text-blue-300/60 italic py-2 px-2 bg-white/5 rounded-md border border-dashed border-white/10" data-id="element-12321">
                      No grouping — showing flat list
                    </p> : <div className="space-y-1" data-id="element-12322">
                      {chain.map((d, i) => <Fragment key={d} data-id="element-12323">
                          <GroupChainChip dimension={d} canRemove={true} onRemove={() => setChain(c => c.filter(x => x !== d))} index={i} isDragging={dragIndex === i} isDropTarget={dropIndex === i && dragIndex !== i} onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} onDragEnd={handleDragEnd} onDragLeave={handleDragLeave} data-id="element-12324" />
                          {i < chain.length - 1 && <div className="flex justify-center py-0.5" data-id="element-12325">
                              <ChevronDownIcon size={12} className="text-blue-300/40" data-id="element-12326" />
                            </div>}
                        </Fragment>)}
                    </div>}
                  <div className="mt-2" data-id="element-12327">
                    <AddLevelDropdown available={available} onAdd={d => setChain(c => [...c, d])} data-id="element-12328" />
                  </div>
                </div>
                {/* Multi-select accordions */}
                <FilterAccordionSection label="Business Unit" options={filterOptions.businessUnit} selected={filters.businessUnit} onChange={next => setFilters(f => ({
              ...f,
              businessUnit: next
            }))} data-id="element-12329" />
                <FilterAccordionSection label="Sales Manager" options={filterOptions.salesManager} selected={filters.salesManager} onChange={next => setFilters(f => ({
              ...f,
              salesManager: next
            }))} data-id="element-12330" />
                <FilterAccordionSection label="Country" options={filterOptions.country} selected={filters.country} onChange={next => setFilters(f => ({
              ...f,
              country: next
            }))} data-id="element-12331" />
                <FilterAccordionSection label="Dealer" options={filterOptions.dealer} selected={filters.dealer} onChange={next => setFilters(f => ({
              ...f,
              dealer: next
            }))} data-id="element-12332" />
                {/* Probability — inline toggle pills (moved to after Dealer) */}
                <div className="border border-white/5 rounded-md bg-white/5 px-3 py-2" data-id="element-12333">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold mb-1.5" data-id="element-12334">
                    Probability
                  </p>
                  <div className="flex flex-wrap gap-1" data-id="element-12335">
                    {([{
                  k: 'high',
                  label: 'High >70%',
                  color: 'emerald'
                }, {
                  k: 'medium',
                  label: 'Medium 30–70%',
                  color: 'amber'
                }, {
                  k: 'low',
                  label: 'Low <30%',
                  color: 'red'
                }] as const).map(b => {
                  const isOn = filters.probabilityBucket.includes(b.k);
                  return <button key={b.k} onClick={() => setFilters(f => ({
                    ...f,
                    probabilityBucket: isOn ? f.probabilityBucket.filter(x => x !== b.k) : [...f.probabilityBucket, b.k]
                  }))} className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border transition-colors ${isOn ? `bg-${b.color}-500/30 border-${b.color}-400/60 text-white` : 'bg-white/5 border-white/10 text-blue-300 hover:text-white hover:bg-white/10'}`} data-id="element-12336">
                          {b.label}
                        </button>;
                })}
                  </div>
                </div>
              </div>
            </div>

            {/* Presets section */}
            <div data-id="element-12337">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold mb-2" data-id="element-12338">
                Presets
              </p>
              <div className="space-y-1" data-id="element-12339">
                {/* Filter preset: Quoted by me */}
                <button onClick={() => setFilters(f => ({
              ...f,
              quotedByMe: !f.quotedByMe
            }))} aria-pressed={filters.quotedByMe} className={`w-full flex items-start gap-2 text-left px-3 py-2 rounded-md border transition-colors ${filters.quotedByMe ? 'bg-blue-500/25 border-blue-400/50' : 'bg-white/5 border-white/5 hover:bg-white/10'}`} data-id="element-12340">
                  <UserIcon size={12} className={`mt-0.5 flex-shrink-0 ${filters.quotedByMe ? 'text-blue-200' : 'text-blue-400/70'}`} data-id="element-12341" />
                  <div className="flex-1 min-w-0" data-id="element-12342">
                    <div className="flex items-center justify-between gap-2" data-id="element-12343">
                      <span className={`text-xs font-medium ${filters.quotedByMe ? 'text-white' : 'text-blue-100'}`} data-id="element-12344">
                        Quoted by me
                      </span>
                      {filters.quotedByMe && <CheckCircleIcon size={12} className="text-blue-300" data-id="element-12345" />}
                    </div>
                    <p className="text-[10px] text-blue-300/60 mt-0.5" data-id="element-12346">
                      Only quotes you created
                    </p>
                  </div>
                </button>
                {/* Chain presets dropdown */}
                {(() => {
              const chainPresets: {
                label: string;
                description: string;
                chain: GroupDimension[];
              }[] = [{
                label: `Default for ${getRoleLabel(role)}`,
                description: 'The grouping the system suggests for this role to manage quotes',
                chain: getDefaultChain(role)
              }, {
                label: 'By Sales Manager',
                description: 'Group quotes by sales manager, then dealer',
                chain: ['salesManager', 'dealer']
              }, {
                label: 'By Dealer',
                description: 'Flat list grouped by dealer',
                chain: ['dealer']
              }, {
                label: 'By Probability',
                description: 'Group by High / Medium / Low close-rate buckets',
                chain: ['probability']
              }, {
                label: 'Flat list',
                description: 'No grouping — show every quote in a row',
                chain: []
              }];
              const chainKey = chain.join('>');
              const activePreset = chainPresets.find(p => p.chain.join('>') === chainKey);
              const triggerLabel = activePreset?.label ?? 'Custom grouping';
              const triggerDescription = activePreset?.description ?? 'Your own grouping configuration';
              const otherPresets = activePreset ? chainPresets.filter(p => p.chain.join('>') !== chainKey) : chainPresets;
              return <div className="relative" data-id="element-12347">
                      <button onClick={() => setChainPresetOpen(o => !o)} aria-haspopup="listbox" aria-expanded={chainPresetOpen} className="w-full flex items-center justify-between gap-2 text-left px-3 py-2 rounded-md bg-white/5 border border-white/5 hover:bg-white/10 transition-colors" data-id="element-12348">
                        <div className="flex-1 min-w-0" data-id="element-12349">
                          <p className="text-xs font-medium text-white truncate" data-id="element-12350">
                            {triggerLabel}
                          </p>
                          <p className="text-[10px] text-blue-300/60 mt-0.5 truncate" data-id="element-12351">
                            {triggerDescription}
                          </p>
                        </div>
                        <ChevronDownIcon size={14} className={`text-blue-300/70 flex-shrink-0 transition-transform ${chainPresetOpen ? 'rotate-180' : ''}`} data-id="element-12352" />
                      </button>
                      {chainPresetOpen && <>
                          <div className="fixed inset-0 z-10" onClick={() => setChainPresetOpen(false)} data-id="element-12353" />
                          <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-slate-900 border border-white/10 rounded-md shadow-lg overflow-hidden" data-id="element-12354">
                            {otherPresets.length === 0 ? <div className="px-3 py-2 text-[11px] text-blue-300/50 italic" data-id="element-12355">
                                No other options
                              </div> : otherPresets.map(p => <button key={p.label} type="button" onClick={() => {
                      setChain(p.chain);
                      setChainPresetOpen(false);
                    }} className="w-full text-left px-3 py-2 hover:bg-white/10 transition-colors" data-id="element-12356">
                                  <p className="text-xs text-blue-100 font-medium" data-id="element-12357">
                                    {p.label}
                                  </p>
                                  <p className="text-[10px] text-blue-300/60 mt-0.5" data-id="element-12358">
                                    {p.description}
                                  </p>
                                </button>)}
                          </div>
                        </>}
                    </div>;
            })()}
              </div>
            </div>

            {/* Sort section */}
            <div data-id="element-12359">
              <div className="flex items-center gap-1.5 mb-2" data-id="element-12360">
                <ArrowUpDownIcon size={12} className="text-blue-400/70" data-id="element-12361" />
                <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12362">
                  Sort by
                </p>
              </div>
              <div className="border border-white/5 rounded-md bg-white/5 px-3 py-2 space-y-2" data-id="element-12363">
                <div className="flex gap-1" data-id="element-12364">
                  {/* Custom dropdown trigger (replaces native select for proper dark theme) */}
                  <div className="relative flex-1 min-w-0" data-id="element-12365">
                    <button onClick={() => setSortOpen(o => !o)} aria-haspopup="listbox" aria-expanded={sortOpen} className="w-full flex items-center justify-between gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded px-2 py-1 text-xs text-white transition-colors" data-id="element-12366">
                      <span className="truncate" data-id="element-12367">
                        {SORT_OPTIONS.find(o => o.key === sort.key)?.label}
                      </span>
                      <ChevronDownIcon size={12} className={`text-blue-300/70 flex-shrink-0 transition-transform ${sortOpen ? 'rotate-180' : ''}`} data-id="element-12368" />
                    </button>
                    {sortOpen && <>
                        <div className="fixed inset-0 z-10" onClick={() => setSortOpen(false)} data-id="element-12369" />
                        <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-slate-900 border border-white/10 rounded-md shadow-lg overflow-hidden max-h-72 overflow-y-auto" data-id="element-12370">
                          {SORT_OPTIONS.map(o => {
                      const isActive = sort.key === o.key;
                      return <button key={o.key} type="button" onClick={() => {
                        setSort(s => ({
                          ...s,
                          key: o.key
                        }));
                        setSortOpen(false);
                      }} className={`w-full text-left px-3 py-2 text-xs transition-colors flex items-center justify-between ${isActive ? 'bg-blue-500/15 text-white' : 'text-blue-100 hover:bg-white/10'}`} data-id="element-12371">
                                <span data-id="element-12372">{o.label}</span>
                                {isActive && <CheckCircleIcon size={12} className="text-blue-300" data-id="element-12373" />}
                              </button>;
                    })}
                        </div>
                      </>}
                  </div>
                  <button onClick={() => setSort(s => ({
                ...s,
                dir: s.dir === 'asc' ? 'desc' : 'asc'
              }))} className="flex-shrink-0 inline-flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded px-2 py-1 text-[11px] text-blue-100 hover:text-white transition-colors" title={`Switch to ${sort.dir === 'asc' ? 'descending' : 'ascending'}`} data-id="element-12374">
                    {sort.dir === 'asc' ? <>
                        <ArrowUpIcon size={11} data-id="element-12375" />
                        Asc
                      </> : <>
                        <ArrowDownIcon size={11} data-id="element-12376" />
                        Desc
                      </>}
                  </button>
                </div>
                <p className="text-[10px] text-blue-300/60" data-id="element-12377">
                  Applies to quote rows inside each group.
                </p>
              </div>
            </div>
            {/* View section */}
            <div data-id="element-12378">
              <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold mb-2" data-id="element-12379">
                View
              </p>
              {/* Tree controls */}
              <div className="grid grid-cols-2 gap-1 mb-3" data-id="element-12380">
                <button onClick={expandAll} className="text-xs text-blue-100 hover:text-white px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/5 transition-colors" data-id="element-12381">
                  Expand all
                </button>
                <button onClick={collapseAll} className="text-xs text-blue-100 hover:text-white px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/5 transition-colors" data-id="element-12382">
                  Collapse all
                </button>
              </div>
              {/* KPI panels customization */}
              <div className="border border-white/5 rounded-md bg-white/5 overflow-hidden" data-id="element-12383">
                <div className="flex items-center justify-between px-3 py-2 border-b border-white/5" data-id="element-12384">
                  <span className="text-xs font-medium text-white" data-id="element-12385">
                    KPI panels
                  </span>
                  <button onClick={() => setKpiPrefs(p => ({
                ...p,
                showKpis: !p.showKpis
              }))} role="switch" aria-checked={kpiPrefs.showKpis} aria-label="Toggle KPI panels visibility" className={`relative w-9 h-5 rounded-full transition-colors ${kpiPrefs.showKpis ? 'bg-blue-500' : 'bg-white/10'}`} data-id="element-12386">
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${kpiPrefs.showKpis ? 'translate-x-[18px]' : 'translate-x-0.5'}`} data-id="element-12387" />
                  </button>
                </div>
                {kpiPrefs.showKpis && <div className="px-2 py-2 flex flex-col gap-1" data-id="element-12388">
                    {kpiPrefs.visibleKpis.length === 0 ? <p className="text-xs text-blue-300/50 italic py-1 px-1" data-id="element-12389">
                        No panels — add one below
                      </p> : kpiPrefs.visibleKpis.map((id, idx) => {
                const isDragging = kpiDragIndex === idx;
                const isDropTarget = kpiDropIndex === idx && kpiDragIndex !== idx;
                return <div key={id} draggable onDragStart={() => handleKpiDragStart(idx)} onDragOver={e => handleKpiDragOver(e, idx)} onDrop={e => handleKpiDrop(e, idx)} onDragEnd={handleKpiDragEnd} className={`flex items-center gap-2 rounded px-2 py-1.5 border select-none cursor-grab active:cursor-grabbing transition-all ${isDragging ? 'opacity-40 bg-blue-500/30 border-blue-400/60' : isDropTarget ? 'bg-blue-500/30 border-blue-300 ring-2 ring-blue-400/40' : 'bg-white/5 border-white/10 hover:bg-white/10'}`} data-id="element-12390">
                            <GripVerticalIcon size={14} className="text-blue-300/60 flex-shrink-0" aria-hidden data-id="element-12391" />
                            <span className="text-xs text-blue-100 flex-1 truncate" data-id="element-12392">
                              {KPI_META[id].label}
                            </span>
                            <button onClick={e => {
                    e.stopPropagation();
                    toggleKpi(id);
                  }} onMouseDown={e => e.stopPropagation()} aria-label={`Remove ${KPI_META[id].label}`} className="text-blue-300/70 hover:text-white rounded-full hover:bg-blue-500/20 w-4 h-4 flex items-center justify-center flex-shrink-0" type="button" data-id="element-12393">
                              ×
                            </button>
                          </div>;
              })}
                    <div className="pt-1" data-id="element-12394">
                      <AddKpiDropdown available={kpisNotVisible} addKpi={addKpi} data-id="element-12395" />
                    </div>
                  </div>}
                <button onClick={resetKpiPrefs} className="w-full text-[10px] uppercase tracking-wider text-blue-300 hover:text-white px-3 py-1.5 border-t border-white/5 hover:bg-white/5 transition-colors" data-id="element-12396">
                  Reset to default
                </button>
              </div>
            </div>
          </div>
        </aside> : null}
    </>;
}
function AddKpiDropdown({
  available,
  addKpi
}: {
  available: KpiId[];
  addKpi: (id: KpiId) => void;
}) {
  const [open, setOpen] = useState(false);
  if (available.length === 0) {
    // All panels added, render disabled button
    return <button disabled className="w-full px-2 py-1.5 text-xs text-blue-300 bg-white/10 rounded border border-white/20 cursor-not-allowed" data-id="element-12397">
        All panels added
      </button>;
  }
  return <div className="relative" data-id="element-12398">
      <button onClick={() => setOpen(!open)} className="w-full px-2 py-1.5 text-xs text-blue-300 bg-white/10 rounded border border-white/20 hover:bg-white/20 transition-colors flex items-center justify-center gap-1" type="button" data-id="element-12399">
        <PlusIcon size={12} data-id="element-12400" />
        Add panel
      </button>
      {open && <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} data-id="element-12401" />
          <div className="absolute left-0 top-full mt-1 z-20 w-40 bg-slate-900 border border-white/10 rounded shadow-lg" data-id="element-12402">
            {available.map(id => <button key={id} onClick={() => {
          addKpi(id);
          setOpen(false);
        }} className="block w-full text-left px-3 py-2 text-sm text-blue-100 hover:bg-white/10 transition-colors" type="button" data-id="element-12403">
                {KPI_META[id].label}
              </button>)}
          </div>
        </>}
    </div>;
}
// --- Business Units View (CEO cascading drilldown) ---
function BusinessUnitsView() {
  const dummyQuotes = useQuotes();
  const [selectedBU, setSelectedBU] = useState<string | null>(null);
  const [selectedSM, setSelectedSM] = useState<string | null>(null);
  const [expandedDealer, setExpandedDealer] = useState<string | null>(null);
  const closedStatuses: QuoteStatus[] = ['Won', 'Lost', 'Expired', 'Cancelled'];
  // Level 1: BUs aggregated
  const buAggregates = useMemo(() => {
    const map = new Map<string, {
      name: string;
      salesManagers: Set<string>;
      dealers: Set<string>;
      countries: Set<string>;
      totalQuotes: number;
      openCount: number;
      wonCount: number;
      lostCount: number;
      openPipeline: number;
      weightedPipeline: number;
      wonAmount: number;
      needsAttention: number;
    }>();
    dummyQuotes.forEach(q => {
      const e = map.get(q.businessUnit) || {
        name: q.businessUnit,
        salesManagers: new Set<string>(),
        dealers: new Set<string>(),
        countries: new Set<string>(),
        totalQuotes: 0,
        openCount: 0,
        wonCount: 0,
        lostCount: 0,
        openPipeline: 0,
        weightedPipeline: 0,
        wonAmount: 0,
        needsAttention: 0
      };
      e.salesManagers.add(q.salesManager);
      e.dealers.add(q.dealer);
      e.countries.add(q.country);
      e.totalQuotes += 1;
      if (!closedStatuses.includes(q.status)) {
        e.openCount += 1;
        e.openPipeline += q.amount;
        e.weightedPipeline += q.amount * q.probability / 100;
      }
      if (q.status === 'Won') {
        e.wonCount += 1;
        e.wonAmount += q.amount;
      }
      if (q.status === 'Lost' || q.status === 'Expired') e.lostCount += 1;
      if (q.needsAttention) e.needsAttention += 1;
      map.set(q.businessUnit, e);
    });
    return Array.from(map.values()).map(b => {
      const decided = b.wonCount + b.lostCount;
      return {
        ...b,
        winRate: decided > 0 ? Math.round(b.wonCount / decided * 100) : 0
      };
    }).sort((a, b) => b.openPipeline - a.openPipeline);
  }, [dummyQuotes]);
  // Level 2: Sales Managers within selected BU
  const smAggregates = useMemo(() => {
    if (!selectedBU) return [];
    const map = new Map<string, {
      name: string;
      dealers: Set<string>;
      totalQuotes: number;
      openCount: number;
      wonCount: number;
      lostCount: number;
      openPipeline: number;
      weightedPipeline: number;
      wonAmount: number;
      needsAttention: number;
      lastActivity: string;
    }>();
    dummyQuotes.filter(q => q.businessUnit === selectedBU).forEach(q => {
      const e = map.get(q.salesManager) || {
        name: q.salesManager,
        dealers: new Set<string>(),
        totalQuotes: 0,
        openCount: 0,
        wonCount: 0,
        lostCount: 0,
        openPipeline: 0,
        weightedPipeline: 0,
        wonAmount: 0,
        needsAttention: 0,
        lastActivity: q.lastUpdated
      };
      e.dealers.add(q.dealer);
      e.totalQuotes += 1;
      if (!closedStatuses.includes(q.status)) {
        e.openCount += 1;
        e.openPipeline += q.amount;
        e.weightedPipeline += q.amount * q.probability / 100;
      }
      if (q.status === 'Won') {
        e.wonCount += 1;
        e.wonAmount += q.amount;
      }
      if (q.status === 'Lost' || q.status === 'Expired') e.lostCount += 1;
      if (q.needsAttention) e.needsAttention += 1;
      if (q.lastUpdated > e.lastActivity) e.lastActivity = q.lastUpdated;
      map.set(q.salesManager, e);
    });
    return Array.from(map.values()).map(s => {
      const decided = s.wonCount + s.lostCount;
      return {
        ...s,
        winRate: decided > 0 ? Math.round(s.wonCount / decided * 100) : 0
      };
    }).sort((a, b) => b.openPipeline - a.openPipeline);
  }, [selectedBU, dummyQuotes]);
  // Level 3: Dealers managed by selected Sales Manager
  const dealerAggregates = useMemo(() => {
    if (!selectedBU || !selectedSM) return [];
    const dealerQuotes = dummyQuotes.filter(q => q.businessUnit === selectedBU && q.salesManager === selectedSM);
    return aggregateByDealer(dealerQuotes);
  }, [selectedBU, selectedSM, dummyQuotes]);
  const getInitials = (name: string) => name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
  return <div className="space-y-6" data-id="element-12404">
      <div className="flex items-start justify-between flex-wrap gap-3" data-id="element-12405">
        <div data-id="element-12406">
          <h2 className="text-xl font-bold text-white" data-id="element-12407">By Business Unit</h2>
          <p className="text-sm text-blue-300/70 mt-0.5" data-id="element-12408">
            Drill down from Business Unit → Sales Manager → Dealer → Quote
          </p>
          <div className="flex items-center gap-1.5 mt-2 text-sm" data-id="element-12409">
            <button onClick={() => {
            setSelectedBU(null);
            setSelectedSM(null);
            setExpandedDealer(null);
          }} className={`flex items-center gap-1.5 px-2 py-1 rounded transition-colors ${selectedBU ? 'text-blue-300 hover:bg-white/10' : 'text-white font-semibold bg-white/5'}`} data-id="element-12410">
              <NetworkIcon size={14} data-id="element-12411" />
              All Business Units
            </button>
            {selectedBU && <>
                <ChevronRightIcon size={13} className="text-blue-300/40" data-id="element-12412" />
                <button onClick={() => {
              setSelectedSM(null);
              setExpandedDealer(null);
            }} className={`flex items-center gap-1.5 px-2 py-1 rounded transition-colors ${selectedSM ? 'text-blue-300 hover:bg-white/10' : 'text-white font-semibold bg-white/5'}`} data-id="element-12413">
                  <Building2Icon size={14} data-id="element-12414" />
                  {selectedBU}
                </button>
              </>}
            {selectedSM && <>
                <ChevronRightIcon size={13} className="text-blue-300/40" data-id="element-12415" />
                <span className="flex items-center gap-1.5 px-2 py-1 rounded text-white font-semibold bg-white/5" data-id="element-12416">
                  <UserIcon size={14} data-id="element-12417" />
                  {selectedSM}
                </span>
              </>}
          </div>
        </div>
      </div>

      {/* LEVEL 1: Business Units grid */}
      {!selectedBU && <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-12418">
          {buAggregates.map(bu => <button key={bu.name} onClick={() => setSelectedBU(bu.name)} className="text-left bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm hover:bg-white/10 hover:border-blue-500/30 transition-all group" data-id="element-12419">
              <div className="flex items-start justify-between mb-4" data-id="element-12420">
                <div className="flex items-center gap-3" data-id="element-12421">
                  <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-blue-500/30 to-indigo-500/20 flex items-center justify-center text-blue-300" data-id="element-12422">
                    <Building2Icon size={22} data-id="element-12423" />
                  </div>
                  <div data-id="element-12424">
                    <h3 className="text-base font-bold text-white leading-tight" data-id="element-12425">
                      {bu.name}
                    </h3>
                    <p className="text-xs text-blue-300/70 mt-0.5" data-id="element-12426">
                      {bu.salesManagers.size} sales managers · {bu.dealers.size}{' '}
                      dealers
                    </p>
                  </div>
                </div>
                {bu.needsAttention > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-xs font-medium" data-id="element-12427">
                    <AlertCircleIcon size={11} data-id="element-12428" />
                    {bu.needsAttention}
                  </span>}
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-12429">
                <div data-id="element-12430">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12431">
                    Open Pipeline
                  </p>
                  <p className="text-lg font-bold text-white mt-0.5" data-id="element-12432">
                    ${bu.openPipeline.toLocaleString()}
                  </p>
                </div>
                <div data-id="element-12433">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12434">
                    Weighted
                  </p>
                  <p className="text-lg font-bold text-white mt-0.5" data-id="element-12435">
                    ${Math.round(bu.weightedPipeline).toLocaleString()}
                  </p>
                </div>
                <div data-id="element-12436">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12437">
                    Quotes
                  </p>
                  <p className="text-sm text-blue-100 mt-0.5" data-id="element-12438">
                    {bu.totalQuotes} total · {bu.openCount} open
                  </p>
                </div>
                <div data-id="element-12439">
                  <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12440">
                    Win Rate
                  </p>
                  <div className="flex items-center gap-2 mt-1" data-id="element-12441">
                    <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden" data-id="element-12442">
                      <div className={`h-full ${bu.winRate >= 60 ? 'bg-emerald-400' : bu.winRate >= 30 ? 'bg-amber-400' : 'bg-red-400'}`} style={{
                  width: `${bu.winRate}%`
                }} data-id="element-12443" />
                    </div>
                    <span className={`text-xs font-semibold ${bu.winRate >= 60 ? 'text-emerald-400' : bu.winRate >= 30 ? 'text-amber-400' : 'text-red-400'}`} data-id="element-12444">
                      {bu.winRate}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-white/5 text-xs text-blue-300/80" data-id="element-12445">
                <span data-id="element-12446">{bu.countries.size} countries</span>
                <span className="flex items-center gap-1 text-blue-400 group-hover:text-white transition-colors" data-id="element-12447">
                  Drill in
                  <ChevronRightIcon size={12} data-id="element-12448" />
                </span>
              </div>
            </button>)}
        </div>}

      {/* LEVEL 2: Sales Managers in selected BU */}
      {selectedBU && !selectedSM && <div className="space-y-4" data-id="element-12449">
          {(() => {
        const bu = buAggregates.find(b => b.name === selectedBU);
        if (!bu) return null;
        return <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-12450">
                <KPICard title="Sales Managers" value={bu.salesManagers.size} icon={<UsersIcon size={20} data-id="element-12452" />} data-id="element-12451" />
                <KPICard title="Open Pipeline" value={`$${bu.openPipeline.toLocaleString()}`} icon={<DollarSignIcon size={20} data-id="element-12454" />} data-id="element-12453" />
                <KPICard title="Weighted Pipeline" value={`$${Math.round(bu.weightedPipeline).toLocaleString()}`} icon={<TrendingUpIcon size={20} data-id="element-12456" />} data-id="element-12455" />
                <KPICard title="Needs Attention" value={bu.needsAttention} subtitle="Across all SMs" icon={<AlertCircleIcon size={20} data-id="element-12458" />} data-id="element-12457" />
              </div>;
      })()}
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden backdrop-blur-sm" data-id="element-12459">
            <div className="px-5 py-3 border-b border-white/10 bg-black/20" data-id="element-12460">
              <h3 className="text-sm font-semibold text-white" data-id="element-12461">
                Sales Managers in {selectedBU}
              </h3>
            </div>
            <div className="divide-y divide-white/5" data-id="element-12462">
              {smAggregates.map(sm => <button key={sm.name} onClick={() => setSelectedSM(sm.name)} className="w-full flex items-center gap-4 px-5 py-4 hover:bg-white/10 transition-colors text-left group" data-id="element-12463">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center ring-2 ring-blue-400/20 flex-shrink-0" data-id="element-12464">
                    <span className="text-sm font-bold text-white" data-id="element-12465">
                      {getInitials(sm.name)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-12466">
                    <div className="flex items-center gap-2" data-id="element-12467">
                      <p className="text-sm font-semibold text-white truncate" data-id="element-12468">
                        {sm.name}
                      </p>
                      {sm.needsAttention > 0 && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px] font-medium" data-id="element-12469">
                          <AlertCircleIcon size={9} data-id="element-12470" />
                          {sm.needsAttention}
                        </span>}
                    </div>
                    <p className="text-xs text-blue-300/60 mt-0.5" data-id="element-12471">
                      {sm.dealers.size} dealers · {sm.totalQuotes} quotes · last
                      activity {sm.lastActivity}
                    </p>
                  </div>
                  <div className="hidden md:grid grid-cols-3 gap-6 flex-shrink-0" data-id="element-12472">
                    <div className="text-right" data-id="element-12473">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12474">
                        Open
                      </p>
                      <p className="text-sm font-bold text-white mt-0.5" data-id="element-12475">
                        ${sm.openPipeline.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-12476">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12477">
                        Weighted
                      </p>
                      <p className="text-sm font-bold text-blue-100 mt-0.5" data-id="element-12478">
                        ${Math.round(sm.weightedPipeline).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-12479">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12480">
                        Win Rate
                      </p>
                      <p className={`text-sm font-bold mt-0.5 ${sm.winRate >= 60 ? 'text-emerald-400' : sm.winRate >= 30 ? 'text-amber-400' : 'text-red-400'}`} data-id="element-12481">
                        {sm.winRate}%
                      </p>
                    </div>
                  </div>
                  <ChevronRightIcon size={18} className="text-blue-300/40 group-hover:text-blue-300 group-hover:translate-x-1 transition-all flex-shrink-0" data-id="element-12482" />
                </button>)}
            </div>
          </div>
        </div>}

      {/* LEVEL 3: Dealers managed by selected SM */}
      {selectedBU && selectedSM && <div className="space-y-4" data-id="element-12483">
          {(() => {
        const sm = smAggregates.find(s => s.name === selectedSM);
        if (!sm) return null;
        return <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-12484">
                <KPICard title="Dealers Managed" value={sm.dealers.size} icon={<BuildingIcon size={20} data-id="element-12486" />} data-id="element-12485" />
                <KPICard title="Open Pipeline" value={`$${sm.openPipeline.toLocaleString()}`} icon={<DollarSignIcon size={20} data-id="element-12488" />} data-id="element-12487" />
                <KPICard title="Won (YTD)" value={`$${sm.wonAmount.toLocaleString()}`} icon={<CheckCircleIcon size={20} data-id="element-12490" />} data-id="element-12489" />
                <KPICard title="Win Rate" value={`${sm.winRate}%`} icon={<TrendingUpIcon size={20} data-id="element-12492" />} data-id="element-12491" />
              </div>;
      })()}
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden backdrop-blur-sm" data-id="element-12493">
            <div className="px-5 py-3 border-b border-white/10 bg-black/20" data-id="element-12494">
              <h3 className="text-sm font-semibold text-white" data-id="element-12495">
                Dealers under {selectedSM}
              </h3>
            </div>
            <div className="overflow-x-auto" data-id="element-12496">
              <table className="w-full text-sm text-left" data-id="element-12497">
                <thead className="text-xs text-blue-300 uppercase bg-black/10 border-b border-white/10" data-id="element-12498">
                  <tr data-id="element-12499">
                    <th className="px-4 py-3 font-medium w-8" data-id="element-12500"></th>
                    <th className="px-4 py-3 font-medium" data-id="element-12501">Dealer</th>
                    <th className="px-4 py-3 font-medium" data-id="element-12502">Country</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-12503">Quotes</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-12504">Open</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-12505">
                      Open Pipeline
                    </th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-12506">
                      Weighted
                    </th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-12507">Won</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-12508">
                      Win Rate
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-12509">
                  {dealerAggregates.map(dealer => {
                const isExpanded = expandedDealer === dealer.name;
                return <Fragment key={dealer.name} data-id="element-12510">
                        <tr onClick={() => setExpandedDealer(isExpanded ? null : dealer.name)} className={`border-b border-white/5 transition-colors cursor-pointer ${isExpanded ? 'bg-blue-500/10' : 'hover:bg-white/10'}`} data-id="element-12511">
                          <td className="px-4 py-3 text-blue-300" data-id="element-12512">
                            <ChevronRightIcon size={16} className={`transition-transform ${isExpanded ? 'rotate-90 text-blue-400' : ''}`} data-id="element-12513" />
                          </td>
                          <td className="px-4 py-3" data-id="element-12514">
                            <div className="flex items-center gap-3" data-id="element-12515">
                              <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-300 flex-shrink-0" data-id="element-12516">
                                <BuildingIcon size={16} data-id="element-12517" />
                              </div>
                              <p className="text-sm font-semibold text-white truncate" data-id="element-12518">
                                {dealer.name}
                              </p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-blue-200" data-id="element-12519">
                            <div className="flex items-center gap-1.5" data-id="element-12520">
                              <GlobeIcon size={12} className="text-blue-400/60 flex-shrink-0" data-id="element-12521" />
                              {dealer.country}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-white font-medium" data-id="element-12522">
                            {dealer.totalQuotes}
                          </td>
                          <td className="px-4 py-3 text-right text-blue-200" data-id="element-12523">
                            {dealer.openCount}
                          </td>
                          <td className="px-4 py-3 text-right text-white font-medium" data-id="element-12524">
                            ${dealer.openPipeline.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-blue-100" data-id="element-12525">
                            $
                            {Math.round(dealer.weightedPipeline).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-12526">
                            <span className="text-emerald-400 font-medium" data-id="element-12527">
                              {dealer.wonCount}
                            </span>
                            <span className="text-blue-300/40 mx-1" data-id="element-12528">/</span>
                            <span className="text-red-400 font-medium" data-id="element-12529">
                              {dealer.lostCount}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-12530">
                            <span className={`text-xs font-semibold ${dealer.winRate >= 60 ? 'text-emerald-400' : dealer.winRate >= 30 ? 'text-amber-400' : 'text-red-400'}`} data-id="element-12531">
                              {dealer.winRate}%
                            </span>
                          </td>
                        </tr>
                        {isExpanded && <tr className="bg-black/30" data-id="element-12532">
                            <td colSpan={9} className="px-4 py-4" data-id="element-12533">
                              <div className="ml-12 space-y-2" data-id="element-12534">
                                <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold mb-2" data-id="element-12535">
                                  {dealer.quotes.length} Quote
                                  {dealer.quotes.length !== 1 ? 's' : ''}
                                </p>
                                {dealer.quotes.map(q => <div key={q.id} className="flex items-center gap-4 p-2.5 rounded-lg bg-white/5 border border-white/5 hover:bg-white/10 transition-colors" data-id="element-12536">
                                    <span className="text-xs font-mono text-blue-400 w-24 flex-shrink-0" data-id="element-12537">
                                      {q.id}
                                    </span>
                                    <span className="text-sm text-white font-medium flex-1 min-w-0 truncate" data-id="element-12538">
                                      {q.customer}
                                    </span>
                                    <span className="text-xs text-blue-300/70 w-20 text-right" data-id="element-12539">
                                      {q.date}
                                    </span>
                                    <span className="text-sm text-white font-medium w-28 text-right" data-id="element-12540">
                                      ${q.amount.toLocaleString()}
                                    </span>
                                    <span className="text-xs text-blue-300 w-12 text-right" data-id="element-12541">
                                      {q.probability}%
                                    </span>
                                    <div className="w-32 flex justify-end" data-id="element-12542">
                                      <StatusBadge status={q.status} data-id="element-12543" />
                                    </div>
                                  </div>)}
                              </div>
                            </td>
                          </tr>}
                      </Fragment>;
              })}
                </tbody>
              </table>
            </div>
          </div>
        </div>}
    </div>;
}
// --- Dealers View ---
interface DealerAggregate {
  name: string;
  country: string;
  totalQuotes: number;
  openCount: number;
  wonCount: number;
  lostCount: number;
  openPipeline: number;
  weightedPipeline: number;
  wonAmount: number;
  winRate: number;
  needsAttention: number;
  lastActivity: string;
  quotes: Quote[];
}
function aggregateByDealer(quotes: Quote[]): DealerAggregate[] {
  const closedStatuses: QuoteStatus[] = ['Won', 'Lost', 'Expired', 'Cancelled'];
  const map = new Map<string, DealerAggregate>();
  quotes.forEach(q => {
    const existing = map.get(q.dealer) || {
      name: q.dealer,
      country: q.country,
      totalQuotes: 0,
      openCount: 0,
      wonCount: 0,
      lostCount: 0,
      openPipeline: 0,
      weightedPipeline: 0,
      wonAmount: 0,
      winRate: 0,
      needsAttention: 0,
      lastActivity: q.lastUpdated,
      quotes: []
    };
    existing.totalQuotes += 1;
    existing.quotes.push(q);
    if (!closedStatuses.includes(q.status)) {
      existing.openCount += 1;
      existing.openPipeline += q.amount;
      existing.weightedPipeline += q.amount * q.probability / 100;
    }
    if (q.status === 'Won') {
      existing.wonCount += 1;
      existing.wonAmount += q.amount;
    }
    if (q.status === 'Lost' || q.status === 'Expired') {
      existing.lostCount += 1;
    }
    if (q.needsAttention) existing.needsAttention += 1;
    if (q.lastUpdated > existing.lastActivity) existing.lastActivity = q.lastUpdated;
    map.set(q.dealer, existing);
  });
  // Compute win rate
  const result = Array.from(map.values()).map(d => {
    const decided = d.wonCount + d.lostCount;
    return {
      ...d,
      winRate: decided > 0 ? Math.round(d.wonCount / decided * 100) : 0
    };
  });
  // Sort by open pipeline desc
  return result.sort((a, b) => b.openPipeline - a.openPipeline);
}
type DealerGroupBy = 'none' | 'probability' | 'status';
function DealerQuotesPanel({
  quotes,
  groupBy,
  onGroupByChange
}: {
  quotes: Quote[];
  groupBy: DealerGroupBy;
  onGroupByChange: (g: DealerGroupBy) => void;
}) {
  // Build groups based on selected criterion
  const groups = useMemo(() => {
    if (groupBy === 'none') {
      return [{
        key: 'all',
        label: `${quotes.length} Quote${quotes.length !== 1 ? 's' : ''}`,
        accent: 'text-blue-400/60',
        dotColor: 'bg-blue-400/40',
        quotes: quotes
      }];
    }
    if (groupBy === 'probability') {
      const high = quotes.filter(q => q.probability > 70);
      const mid = quotes.filter(q => q.probability >= 30 && q.probability <= 70);
      const low = quotes.filter(q => q.probability < 30);
      return [{
        key: 'high',
        label: 'High (>70%)',
        accent: 'text-emerald-400',
        dotColor: 'bg-emerald-400',
        quotes: high
      }, {
        key: 'mid',
        label: 'Medium (30–70%)',
        accent: 'text-amber-400',
        dotColor: 'bg-amber-400',
        quotes: mid
      }, {
        key: 'low',
        label: 'Low (<30%)',
        accent: 'text-red-400',
        dotColor: 'bg-red-400',
        quotes: low
      }].filter(g => g.quotes.length > 0);
    }
    // groupBy === 'status'
    const order: QuoteStatus[] = ['Draft', 'Active', 'Expired', 'Won', 'Lost', 'Cancelled'];
    return order.map(status => ({
      key: status,
      label: status,
      accent: 'text-blue-300',
      dotColor: 'bg-blue-400/40',
      quotes: quotes.filter(q => q.status === status)
    })).filter(g => g.quotes.length > 0);
  }, [quotes, groupBy]);
  const groupOptions: {
    value: DealerGroupBy;
    label: string;
  }[] = [{
    value: 'none',
    label: 'None'
  }, {
    value: 'probability',
    label: 'Probability'
  }, {
    value: 'status',
    label: 'Status'
  }];
  return <div className="space-y-3" data-id="element-12544">
      {/* Header with group-by selector */}
      <div className="flex items-center justify-between flex-wrap gap-2" data-id="element-12545">
        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12546">
          {quotes.length} Quote{quotes.length !== 1 ? 's' : ''}
        </p>
        <div className="flex items-center gap-2" data-id="element-12547">
          <span className="text-[10px] uppercase tracking-wider text-blue-400/60 font-semibold" data-id="element-12548">
            Group by
          </span>
          <div className="flex bg-white/5 border border-white/10 rounded-lg p-0.5" data-id="element-12549">
            {groupOptions.map(opt => <button key={opt.value} onClick={() => onGroupByChange(opt.value)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${groupBy === opt.value ? 'bg-blue-600 text-white' : 'text-blue-300 hover:text-white hover:bg-white/5'}`} data-id="element-12550">
                {opt.label}
              </button>)}
          </div>
        </div>
      </div>

      {/* Groups */}
      <div className="space-y-4" data-id="element-12551">
        {groups.map(group => {
        const groupTotal = group.quotes.reduce((acc, q) => acc + q.amount, 0);
        const groupWeighted = group.quotes.reduce((acc, q) => acc + q.amount * q.probability / 100, 0);
        return <div key={group.key} data-id="element-12552">
              {groupBy !== 'none' && <div className="flex items-center justify-between mb-2 px-1" data-id="element-12553">
                  <div className="flex items-center gap-2" data-id="element-12554">
                    <span className={`w-1.5 h-1.5 rounded-full ${group.dotColor}`} data-id="element-12555" />
                    <span className={`text-xs font-semibold ${group.accent}`} data-id="element-12556">
                      {group.label}
                    </span>
                    <span className="text-xs text-blue-300/50" data-id="element-12557">
                      · {group.quotes.length} quote
                      {group.quotes.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs" data-id="element-12558">
                    <span className="text-blue-300/60" data-id="element-12559">
                      Total:{' '}
                      <span className="text-white font-medium" data-id="element-12560">
                        ${groupTotal.toLocaleString()}
                      </span>
                    </span>
                    <span className="text-blue-300/60" data-id="element-12561">
                      Weighted:{' '}
                      <span className="text-white font-medium" data-id="element-12562">
                        ${Math.round(groupWeighted).toLocaleString()}
                      </span>
                    </span>
                  </div>
                </div>}
              <div className="space-y-2" data-id="element-12563">
                {group.quotes.map(q => <div key={q.id} className="flex items-center gap-4 p-2.5 rounded-lg bg-white/5 border border-white/5 hover:bg-white/10 transition-colors" data-id="element-12564">
                    <span className="text-xs font-mono text-blue-400 w-24 flex-shrink-0" data-id="element-12565">
                      {q.id}
                    </span>
                    <span className="text-sm text-white font-medium flex-1 min-w-0 truncate" data-id="element-12566">
                      {q.customer}
                    </span>
                    <span className="text-xs text-blue-300/70 w-20 text-right" data-id="element-12567">
                      {q.date}
                    </span>
                    <span className="text-sm text-white font-medium w-28 text-right" data-id="element-12568">
                      ${q.amount.toLocaleString()}
                    </span>
                    <span className="text-xs text-blue-300 w-12 text-right" data-id="element-12569">
                      {q.probability}%
                    </span>
                    <div className="w-32 flex justify-end" data-id="element-12570">
                      <StatusBadge status={q.status} data-id="element-12571" />
                    </div>
                  </div>)}
              </div>
            </div>;
      })}
      </div>
    </div>;
}
function DealersView() {
  const dummyQuotes = useQuotes();
  const [search, setSearch] = useState('');
  const [expandedDealer, setExpandedDealer] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<DealerGroupBy>('none');
  const [sortBy, setSortBy] = useState<'openPipeline' | 'weighted' | 'totalQuotes' | 'winRate'>('openPipeline');
  const dealers = useMemo(() => aggregateByDealer(dummyQuotes), [dummyQuotes]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q ? dealers.filter(d => d.name.toLowerCase().includes(q) || d.country.toLowerCase().includes(q)) : dealers;
    const sorted = [...base].sort((a, b) => {
      if (sortBy === 'openPipeline') return b.openPipeline - a.openPipeline;
      if (sortBy === 'weighted') return b.weightedPipeline - a.weightedPipeline;
      if (sortBy === 'totalQuotes') return b.totalQuotes - a.totalQuotes;
      return b.winRate - a.winRate;
    });
    return sorted;
  }, [dealers, search, sortBy]);
  // Totals across all dealers (unfiltered, for KPI cards)
  const totals = useMemo(() => {
    return dealers.reduce((acc, d) => ({
      dealers: acc.dealers + 1,
      active: acc.active + (d.openCount > 0 ? 1 : 0),
      openPipeline: acc.openPipeline + d.openPipeline,
      weighted: acc.weighted + d.weightedPipeline,
      attention: acc.attention + d.needsAttention
    }), {
      dealers: 0,
      active: 0,
      openPipeline: 0,
      weighted: 0,
      attention: 0
    });
  }, [dealers]);
  return <div className="space-y-6" data-id="element-12572">
      <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-12573">
        <div data-id="element-12574">
          <h2 className="text-xl font-bold text-white" data-id="element-12575">Quotes by Dealer</h2>
          <p className="text-sm text-blue-300/70 mt-0.5" data-id="element-12576">
            Pipeline performance grouped by dealer
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-12577">
          <div className="relative" data-id="element-12578">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300/50" data-id="element-12579" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search dealer..." className="bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 w-64" data-id="element-12580" />
          </div>
          <div className="relative" data-id="element-12581">
            <select value={sortBy} onChange={e => setSortBy(e.target.value as typeof sortBy)} className="appearance-none bg-white/5 border border-white/10 rounded-lg pl-3 pr-9 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-12582">
              <option value="openPipeline" className="bg-slate-800" data-id="element-12583">
                Sort: Open Pipeline
              </option>
              <option value="weighted" className="bg-slate-800" data-id="element-12584">
                Sort: Weighted
              </option>
              <option value="totalQuotes" className="bg-slate-800" data-id="element-12585">
                Sort: Quote Count
              </option>
              <option value="winRate" className="bg-slate-800" data-id="element-12586">
                Sort: Win Rate
              </option>
            </select>
            <ChevronRightIcon size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-blue-300 rotate-90 pointer-events-none" data-id="element-12587" />
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-12588">
        <KPICard title="Active Dealers" value={`${totals.active} / ${totals.dealers}`} subtitle="With open quotes" icon={<BuildingIcon size={20} data-id="element-12590" />} data-id="element-12589" />
        <KPICard title="Open Pipeline" value={`${totals.openPipeline.toLocaleString()}`} icon={<DollarSignIcon size={20} data-id="element-12592" />} data-id="element-12591" />
        <KPICard title="Weighted Pipeline" value={`${Math.round(totals.weighted).toLocaleString()}`} icon={<TrendingUpIcon size={20} data-id="element-12594" />} data-id="element-12593" />
        <KPICard title="Needs Attention" value={totals.attention} subtitle="Across all dealers" icon={<AlertCircleIcon size={20} data-id="element-12596" />} data-id="element-12595" />
      </div>

      {/* Dealer table */}
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden backdrop-blur-sm" data-id="element-12597">
        <div className="overflow-x-auto" data-id="element-12598">
          <table className="w-full text-sm text-left" data-id="element-12599">
            <thead className="text-xs text-blue-300 uppercase bg-black/20 border-b border-white/10" data-id="element-12600">
              <tr data-id="element-12601">
                <th className="px-4 py-3 font-medium w-8" data-id="element-12602"></th>
                <th className="px-4 py-3 font-medium" data-id="element-12603">Dealer</th>
                <th className="px-4 py-3 font-medium" data-id="element-12604">Country</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-12605">Quotes</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-12606">Open</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-12607">
                  Open Pipeline
                </th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-12608">Weighted</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-12609">Won</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-12610">Win Rate</th>
                <th className="px-4 py-3 font-medium text-center" data-id="element-12611">Alerts</th>
              </tr>
            </thead>
            <tbody data-id="element-12612">
              {filtered.length === 0 && <tr data-id="element-12613">
                  <td colSpan={10} className="px-4 py-10 text-center text-sm text-blue-300/60" data-id="element-12614">
                    No dealers match your search.
                  </td>
                </tr>}
              {filtered.map(dealer => {
              const isExpanded = expandedDealer === dealer.name;
              return <Fragment key={dealer.name} data-id="element-12615">
                    <tr onClick={() => setExpandedDealer(isExpanded ? null : dealer.name)} className={`border-b border-white/5 transition-colors cursor-pointer ${isExpanded ? 'bg-blue-500/10' : 'hover:bg-white/10'}`} data-id="element-12616">
                      <td className="px-4 py-3 text-blue-300" data-id="element-12617">
                        <ChevronRightIcon size={16} className={`transition-transform ${isExpanded ? 'rotate-90 text-blue-400' : ''}`} data-id="element-12618" />
                      </td>
                      <td className="px-4 py-3" data-id="element-12619">
                        <div className="flex items-center gap-3" data-id="element-12620">
                          <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-300 flex-shrink-0" data-id="element-12621">
                            <BuildingIcon size={16} data-id="element-12622" />
                          </div>
                          <div className="min-w-0" data-id="element-12623">
                            <p className="text-sm font-semibold text-white truncate" data-id="element-12624">
                              {dealer.name}
                            </p>
                            <p className="text-xs text-blue-300/60 truncate" data-id="element-12625">
                              Last activity: {dealer.lastActivity}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-blue-200" data-id="element-12626">
                        <div className="flex items-center gap-1.5" data-id="element-12627">
                          <GlobeIcon size={12} className="text-blue-400/60 flex-shrink-0" data-id="element-12628" />
                          {dealer.country}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-white font-medium" data-id="element-12629">
                        {dealer.totalQuotes}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-200" data-id="element-12630">
                        {dealer.openCount}
                      </td>
                      <td className="px-4 py-3 text-right text-white font-medium" data-id="element-12631">
                        ${dealer.openPipeline.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-100" data-id="element-12632">
                        ${Math.round(dealer.weightedPipeline).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-12633">
                        <span className="text-emerald-400 font-medium" data-id="element-12634">
                          {dealer.wonCount}
                        </span>
                        <span className="text-blue-300/40 mx-1" data-id="element-12635">/</span>
                        <span className="text-red-400 font-medium" data-id="element-12636">
                          {dealer.lostCount}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-12637">
                        <div className="inline-flex items-center gap-2 min-w-[80px] justify-end" data-id="element-12638">
                          <div className="w-12 h-1.5 rounded-full bg-white/10 overflow-hidden flex-shrink-0" data-id="element-12639">
                            <div className={`h-full ${dealer.winRate >= 60 ? 'bg-emerald-400' : dealer.winRate >= 30 ? 'bg-amber-400' : 'bg-red-400'}`} style={{
                          width: `${dealer.winRate}%`
                        }} data-id="element-12640" />
                          </div>
                          <span className={`text-xs font-semibold w-9 text-right ${dealer.winRate >= 60 ? 'text-emerald-400' : dealer.winRate >= 30 ? 'text-amber-400' : 'text-red-400'}`} data-id="element-12641">
                            {dealer.winRate}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center" data-id="element-12642">
                        {dealer.needsAttention > 0 ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-xs font-medium" data-id="element-12643">
                            <AlertCircleIcon size={11} data-id="element-12644" />
                            {dealer.needsAttention}
                          </span> : <span className="text-blue-300/30" data-id="element-12645">—</span>}
                      </td>
                    </tr>
                    {isExpanded && <tr className="bg-black/30" data-id="element-12646">
                        <td colSpan={10} className="px-4 py-4" data-id="element-12647">
                          <div className="ml-12" data-id="element-12648">
                            <DealerQuotesPanel quotes={dealer.quotes} groupBy={groupBy} onGroupByChange={setGroupBy} data-id="element-12649" />
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
function PlaceholderView({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <div className="flex-1 flex items-center justify-center min-h-[60vh]" data-id="element-12650">
      <div className="text-center max-w-md" data-id="element-12651">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/5 border border-white/10 text-blue-400 shadow-lg mb-4" data-id="element-12652">
          <ClockIcon size={24} data-id="element-12653" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2" data-id="element-12654">{title}</h2>
        <p className="text-sm text-blue-300/70 leading-relaxed" data-id="element-12655">
          {description}
        </p>
      </div>
    </div>;
}
// --- Main Component ---
export function QuotesPageV2() {
  const {
    role
  } = useUser();
  const [activeNav, setActiveNav] = useState<NavItem>('quotes');
  const [configOpen, setConfigOpen] = useState(true);
  const explorerActive = activeNav === 'quotes';
  const navItems = [{
    id: 'quotes',
    label: 'Quote Explorer',
    icon: <ListIcon size={16} data-id="element-12656" />
  }, {
    id: 'overview',
    label: 'Overview',
    icon: <LayoutDashboardIcon size={16} data-id="element-12657" />
  }, {
    id: 'pipeline',
    label: 'Pipeline',
    icon: <KanbanSquareIcon size={16} data-id="element-12658" />
  }, {
    id: 'attention',
    label: 'Needs Attention',
    icon: <AlertCircleIcon size={16} data-id="element-12659" />
  }];
  return <div className="flex flex-col h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 overflow-hidden" data-id="element-12660">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0" data-id="element-12661">
        <div className="absolute top-[10%] left-[5%] w-64 h-64 bg-blue-600 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" data-id="element-12662"></div>
        <div className="absolute top-[40%] right-[10%] w-72 h-72 bg-purple-600 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" data-id="element-12663"></div>
        <div className="absolute bottom-[10%] left-[25%] w-80 h-80 bg-teal-600 rounded-full mix-blend-multiply filter blur-[100px] opacity-20" data-id="element-12664"></div>
      </div>

      {/* Header with breadcrumb + top-level tabs */}
      <div className="bg-gradient-to-r from-blue-950/80 via-indigo-950/60 to-purple-950/50 backdrop-blur-sm border-b border-white/10 px-6 relative z-10 flex-shrink-0 h-[72px] flex items-center" data-id="element-12665">
        {/* Breadcrumb (left) */}
        <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-12666">
          <HomeIcon size={15} className="text-blue-300/60 hover:text-blue-200 cursor-pointer transition-colors flex-shrink-0" data-id="element-12667" />
          <ChevronRightIcon size={13} className="text-blue-300/40 flex-shrink-0" data-id="element-12668" />
          <span className="text-xs text-blue-300/60" data-id="element-12669">Sales Pipeline</span>
        </div>
        {/* Top tabs — centered */}
        <nav className="absolute left-1/2 -translate-x-1/2 flex items-stretch gap-1 h-full" aria-label="Quotes navigation" data-id="element-12670">
          {navItems.map(item => {
          const isActive = activeNav === item.id;
          return <button key={item.id} onClick={() => setActiveNav(item.id as NavItem)} className={`relative flex items-center gap-2 px-4 text-sm font-medium transition-colors border-b-2 ${isActive ? 'text-white border-blue-400' : 'text-blue-300/70 hover:text-white border-transparent hover:border-white/20'}`} data-id="element-12671">
                <span className={isActive ? 'text-blue-300' : 'text-blue-400/60'} data-id="element-12672">
                  {item.icon}
                </span>
                {item.label}
                {item.id === 'attention' && <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-id="element-12673">
                    1
                  </span>}
              </button>;
        })}
        </nav>
      </div>

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden relative z-10" data-id="element-12674">
        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 no-scrollbar transition-[padding] duration-200" style={{
        paddingRight: explorerActive && configOpen ? 288 : undefined
      }} data-id="element-12675">
          <div className="max-w-7xl mx-auto h-full" data-id="element-12676">
            {activeNav === 'overview' && <OverviewView role={role} data-id="element-12677" />}
            {activeNav === 'pipeline' && <PipelineView data-id="element-12678" />}
            {activeNav === 'quotes' && <AllQuotesExplorerView role={role} configOpen={configOpen} setConfigOpen={setConfigOpen} data-id="element-12679" />}
            {activeNav === 'attention' && <PlaceholderView title="Needs Attention" description="Quotes that require your immediate action, such as expiring validity, missing information, or stalled negotiations." data-id="element-12680" />}
          </div>
        </div>
      </div>
    </div>;
}